import React, {useCallback, useEffect, useMemo, useReducer, useRef, useState} from 'react';
import {
    AreaSeries,
    BaselineSeries,
    CandlestickData,
    CandlestickSeries,
    ColorType,
    createChart,
    createSeriesMarkers,
    HistogramData,
    HistogramSeries,
    IChartApi,
    ISeriesApi,
    ISeriesMarkersPluginApi,
    LineData,
    LineSeries,
    Time,
} from 'lightweight-charts';
import {Button, InputNumber, message, Spin} from 'antd';
import {
    detectPatterns,
    getAIAnalysis,
    getBacktestStats,
    getPatternList,
    getPatternResults,
    getZoneAIAnalysis,
    getZoneResults,
    triggerAIAnalysis,
    triggerZoneAIAnalysis
} from '../../services/patternRecognitionService';
import type {ApiKey, CompleteChartData, MarkPriceCandle, OrderRequest} from '../../services/tradingService';
import {getActiveApiKeys, getRealtimePrice, tradingService} from '../../services/tradingService';
import api from '../../services/api';
import type {
    AlgorithmPatternRawData,
    BacktestStats,
    PatternDetectResult,
    ZoneDetectResult
} from '../../types/patternRecognition';
import {useAuth} from '../../contexts/AuthContext';
import './PatternRecognitionPage.css';
import {createInitialAiState, patternAiReducer} from './pattern/state/patternAiReducer';
import {usePatternCapabilities} from './pattern/hooks/usePatternCapabilities';
import {useMultiAiTradePlanOverlay} from './pattern/hooks/useMultiAiTradePlanOverlay';
import {useTrendPatternOverlay} from './pattern/hooks/useTrendPatternOverlay';
import {useSignalExpiry} from './pattern/hooks/useSignalExpiry';
import {selectActiveTradePlans, selectCapabilities} from './pattern/state/patternAiSelectors';
import {AiQuickTradingPanel} from './pattern/components/AiQuickTradingPanel';
import {PendingSignalOrderPanel} from './pattern/components/PendingSignalOrderPanel';
import {PatternSignalCard} from './pattern/components/PatternSignalCard';
import {TrendPatternCard} from './pattern/components/TrendPatternCard';
import {AlgorithmPatternCard} from './pattern/components/AlgorithmPatternCard';
import {ZoneSignalCard} from './pattern/components/ZoneSignalCard';
import type {AIAnalysis, AnalysisKey, SignalTradeDraft} from './pattern/types';
import {checkAllExpiry} from './pattern/utils/signalExpiry';
import {
    calculateProfitLossRatio,
    createTradeDrafts,
    normalizeTradeDraftPrices,
    validateTradePlanPrices,
} from './pattern/utils/tradeDrafts';
import {toPosSide, validatePriceRelation} from './pattern/utils/tradeDirection';
import {formatSignalTime} from './pattern/utils/patternTime';
import {resolveTouchedFibonacciRatio} from './pattern/utils/patternOverlayLines';
import {formatSuccessfulOrderStatus} from './pattern/utils/submissionErrors';
import {appendFutureCandleWhitespace} from './pattern/utils/futureCandleWhitespace';
import {showTradingConfirmDialog} from '../../components/TradingConfirmDialog';
import {apiConnectionService} from '../../services/apiConnectionService';

// ═══════════════════════════════
// 常量
// ═══════════════════════════════

const INTERVALS = ['5m', '1H', '4H', '1D'] as const;

const KLINE_COUNT: Record<string, number> = {'5m': 192, '1H': 168, '4H': 30, '1D': 24};

const MAX_SELECTION = 5;

function intervalMilliseconds(value: string): number {
    const match = /^(\d+)(m|H|D)$/.exec(value);
    if (!match) return 60 * 60 * 1000;
    const unit = 'm' === match[2] ? 60 * 1000 : 'H' === match[2] ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
    return Number(match[1]) * unit;
}

function timestampMillis(value: number): number {
    return value > 10_000_000_000 ? value : value * 1000;
}

function isValidAiPriceRelation(analysis: AIAnalysis, signalDirection: 1 | -1 | 0): boolean {
    if (hasMissingAiPrices(analysis)) return false;
    const entryPrice = analysis.entryPrice!;
    const targetPrice = analysis.targetPrice!;
    const stopLossPrice = analysis.stopLossPrice!;
    const side = toPosSide(signalDirection);
    if (side) {
        return validatePriceRelation(side, entryPrice, targetPrice, stopLossPrice);
    }
    return validatePriceRelation('long', entryPrice, targetPrice, stopLossPrice)
        || validatePriceRelation('short', entryPrice, targetPrice, stopLossPrice);
}

function hasMissingAiPrices(analysis: AIAnalysis): boolean {
    return [analysis.entryPrice, analysis.targetPrice, analysis.stopLossPrice]
        .some(value => null == value);
}

function canOpenAiTradeDraft(analysis: AIAnalysis, signalDirection: 1 | -1 | 0): boolean {
    return hasMissingAiPrices(analysis) || isValidAiPriceRelation(analysis, signalDirection);
}

// ── 浏览器存储 ──
const STORAGE_KEY = 'pt_config';

function loadConfig(): {
    mode: 'SPOT' | 'SWAP';
    symbol: string;
    interval: string;
    indicatorConfigs: IndicatorItemConfig[]
} | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (null == raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function saveConfig(config: {
    mode: string;
    symbol: string;
    interval: string;
    indicatorConfigs: IndicatorItemConfig[]
}) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch {
        /* 浏览器禁用存储时仅跳过持久化。 */
    }
}

type IndicatorPeriodConfig = { value: string; label: string; checked: boolean; color: string };
type IndicatorItemConfig = { key: string; label: string; pane: 'main' | 'sub'; periods: IndicatorPeriodConfig[] };

const DEFAULT_INDICATOR_CONFIGS: IndicatorItemConfig[] = [
    {
        key: 'SMA', label: 'SMA', pane: 'main', periods: [
            {value: '7', label: '7', checked: true, color: '#4d9fff'},
            {value: '25', label: '25', checked: true, color: '#a78bfa'},
        ]
    },
    {
        key: 'BOLL', label: 'BOLL', pane: 'main', periods: [
            {value: '20_2.0', label: '20,2', checked: true, color: '#f5a623'},
        ]
    },
    {
        key: 'RSI', label: 'RSI', pane: 'sub', periods: [
            {value: '14', label: '14', checked: true, color: '#00d97e'},
        ]
    },
    {
        key: 'MACD', label: 'MACD', pane: 'sub', periods: [
            {value: '12_26_9', label: '12,26,9', checked: true, color: '#4d9fff'},
        ]
    },
];
const TOP_COINS = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK', 'DOT'];
const POC_SELECTABLE_COINS = ['BTC', 'ETH', 'SOL', 'SUI', 'ADA'];

function buildSwapSymbols(): string[] {
    return TOP_COINS.map(c => `${c}-USDT-SWAP`);
}

function buildSpotSymbols(): string[] {
    return TOP_COINS.map(c => `${c}-USDT`);
}

const SYMBOL_DISPLAY: Record<string, string> = {};
TOP_COINS.forEach(c => {
    SYMBOL_DISPLAY[`${c}-USDT-SWAP`] = `${c}/USDT`;
    SYMBOL_DISPLAY[`${c}-USDT`] = `${c}/USDT`;
});
const CHART_COLORS = {
    bg: '#080b0f',
    text: '#3d5469',
    grid: '#1e2d3d',
    bull: '#00d97e',
    bear: '#ff5252',
    amber: '#f5a623',
    blue: '#4d9fff',
    purple: '#a78bfa',
    volUp: 'rgba(0,217,126,0.5)',
    volDown: 'rgba(255,82,82,0.5)',
    volDoji: 'rgba(245,166,35,0.5)',
};

// ═══════════════════════════════
// 工具函数
// ═══════════════════════════════

function formatPrice(v: number | null | undefined): string {
    if (null == v || !Number.isFinite(v)) return '--';
    if (v >= 1000) return v.toLocaleString('en-US', {maximumFractionDigits: 2});
    if (v >= 1) return v.toFixed(2);
    return v.toFixed(4);
}

/** 根据周期格式化时间轴刻度 */
/** 时间戳规范化：毫秒→秒（>10位数按毫秒处理），确保OHLC与指标对齐 */
function toUtcSeconds(ts: number): number {
    if (ts > 10000000000) return Math.floor(ts / 1000);
    return Math.floor(ts);
}

function formatTick(utcSeconds: number, interval: string): string {
    const d = new Date(utcSeconds * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    if ('1D' === interval) {
        return `${String(d.getFullYear()).slice(2)}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }
    if ('4H' === interval || '1H' === interval) {
        return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:00`;
    }
    // 5m, 15m, 1m
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function directionClass(dir: number): string {
    if (1 === dir) return 'bull';
    if (-1 === dir) return 'bear';
    return 'neutral';
}

function directionLabel(dir: number): string {
    if (1 === dir) return '看多';
    if (-1 === dir) return '看空';
    return '中性';
}

function directionArrow(dir: number): string {
    if (1 === dir) return '↑';
    if (-1 === dir) return '↓';
    return '';
}

/** 通用：从 indicators 多周期数据中提取 LineData */
function extractIndicatorLineData(
    indicators: CompleteChartData['indicators'] | undefined,
    indKey: string, mpvKeyPrefix: string, period: number
): LineData[] {
    if (!indicators) return [];
    const ind = indicators[indKey];
    if (!ind?.values?.length) return [];
    const key = `${mpvKeyPrefix}_${period}`;
    const result: LineData[] = [];
    for (const pt of ind.values) {
        const mpv = pt.multiPeriodValues;
        if (mpv && null != (mpv as any)[key]) {
            result.push({time: toUtcSeconds(pt.timestamp) as Time, value: (mpv as any)[key]});
        }
    }
    result.sort((a, b) => (a.time as number) - (b.time as number));
    return result;
}

function indicatorsToSmaData(indicators: CompleteChartData['indicators'] | undefined, period: number): LineData[] {
    return extractIndicatorLineData(indicators, 'SMA', 'sma', period);
}

function indicatorsToRsiData(indicators: CompleteChartData['indicators'] | undefined, period: number): LineData[] {
    return extractIndicatorLineData(indicators, 'RSI', 'rsi', period);
}

/** 在指标数据中按时间戳查找值 */
function getIndValAt(indicators: CompleteChartData['indicators'] | undefined, indKey: string, mpvKey: string, timeMs: number): number | null {
    if (!indicators) return null;
    const ind = indicators[indKey];
    if (!ind?.values) return null;
    for (const pt of ind.values) {
        if (toUtcSeconds(pt.timestamp) === toUtcSeconds(timeMs)) {
            const mpv = pt.multiPeriodValues;
            if (mpv) {
                const v = (mpv as any)[mpvKey];
                if (null != v) return Number(v);
            }
            return null;
        }
    }
    return null;
}

/** 在 MACD 指标数据中按时间戳查找值，支持指定周期参数 */
function getMacdValAt(indicators: CompleteChartData['indicators'] | undefined, timeMs: number, field: 'dif' | 'dea' | 'macd', macdParamKey?: string): number | null {
    if (!indicators) return null;
    const macd = indicators['MACD'];
    if (!macd?.values) return null;
    const normalizedTarget = macdParamKey ? normalizeMacdKey(`macd_${macdParamKey}`) : null;
    for (const pt of macd.values) {
        if (toUtcSeconds(pt.timestamp) === toUtcSeconds(timeMs)) {
            const mpv = pt.multiPeriodValues;
            if (mpv) {
                for (const k of Object.keys(mpv)) {
                    if (!k.startsWith('macd_')) continue;
                    if (null != normalizedTarget && normalizeMacdKey(k) !== normalizedTarget) continue;
                    const val = (mpv as any)[k] as { diff?: number; dea?: number; macd?: number } | undefined;
                    if (val) {
                        if ('dif' === field) return val.diff ?? null;
                        if ('dea' === field) return val.dea ?? null;
                        return val.macd ?? null;
                    }
                }
            }
            return null;
        }
    }
    return null;
}

/** 在 BOLL 指标数据中按时间戳查找中轨值 */
function getBollMidAt(indicators: CompleteChartData['indicators'] | undefined, timeMs: number): number | null {
    if (!indicators) return null;
    const boll = indicators['BOLL'];
    if (!boll?.values) return null;
    for (const pt of boll.values) {
        if (toUtcSeconds(pt.timestamp) === toUtcSeconds(timeMs)) {
            const mpv = pt.multiPeriodValues;
            if (mpv) {
                for (const k of Object.keys(mpv)) {
                    if (k.startsWith('boll_')) {
                        const val = (mpv as any)[k] as { middle?: number } | undefined;
                        if (val?.middle) return val.middle;
                    }
                }
            }
            return null;
        }
    }
    return null;
}

/** 归一化 MACD key：去掉前缀，统一分隔符为逗号 */
function normalizeMacdKey(raw: string): string {
    return raw.replace(/^macd_/, '').replace(/[,;:_]/g, ',');
}

/** 从 indicators 提取指定 MACD 参数的数据 */
function indicatorsToMacdData(indicators: CompleteChartData['indicators'] | undefined, macdParamKey: string): {
    dif: LineData[];
    dea: LineData[];
    macd: HistogramData[]
} | null {
    if (!indicators) return null;
    const macd = indicators['MACD'];
    if (!macd?.values?.length) return null;
    const normalizedParam = normalizeMacdKey(macdParamKey);
    let macdKey: string | null = null;
    for (const pt of macd.values) {
        if (pt.multiPeriodValues) {
            for (const k of Object.keys(pt.multiPeriodValues)) {
                if (k.startsWith('macd_') && normalizeMacdKey(k) === normalizedParam) {
                    macdKey = k;
                    break;
                }
            }
        }
        if (macdKey) break;
    }
    if (null == macdKey) return null;
    const dif: LineData[] = [];
    const dea: LineData[] = [];
    const macdBars: HistogramData[] = [];
    for (const pt of macd.values) {
        const val = pt.multiPeriodValues?.[macdKey] as { diff?: number; dea?: number; macd?: number } | undefined;
        if (val && null != val.diff) {
            const t = toUtcSeconds(pt.timestamp) as Time;
            dif.push({time: t, value: val.diff});
            if (null != val.dea) dea.push({time: t, value: val.dea});
            const m = val.macd;
            if (null != m) {
                macdBars.push({time: t, value: m, color: m >= 0 ? 'rgba(0,217,126,0.5)' : 'rgba(255,82,82,0.5)'});
            }
        }
    }
    if (0 === dif.length) return null;
    dif.sort((a, b) => (a.time as number) - (b.time as number));
    dea.sort((a, b) => (a.time as number) - (b.time as number));
    macdBars.sort((a, b) => (a.time as number) - (b.time as number));
    return {dif, dea, macd: macdBars};
}

/** 从 indicators map 中提取最新值 */
function indicatorLatest(indicators: CompleteChartData['indicators'] | undefined, key: string): string | null {
    if (!indicators) return null;
    const ind = indicators[key];
    if (!ind?.values?.length) return null;
    const latest = ind.values[ind.values.length - 1];
    const val = latest.value;
    if (null == val) return null;
    return Number(val).toFixed('MACD' === key ? 1 : 1);
}

/** MACD 值颜色 */
function indicatorMacdColor(indicators: CompleteChartData['indicators'] | undefined): string {
    if (!indicators) return 'var(--pt-text-dim)';
    const val = indicatorLatest(indicators, 'MACD');
    if (null == val) return 'var(--pt-text-dim)';
    return Number(val) >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)';
}

/** 从 indicators 提取 BOLL 上/中/下轨 LineData（数据在 multiPeriodValues 中） */
function normalizeBollParam(value: string): string {
    const parts = value.replace(/^boll_/i, '').split(/[_,]/);
    if (parts.length < 2) return value;
    return `${Number(parts[0])}_${Number(parts[1])}`;
}

function indicatorsToBollData(indicators: CompleteChartData['indicators'] | undefined, requestedParam: string): {
    upper: LineData[];
    middle: LineData[];
    lower: LineData[]
} | null {
    if (!indicators) return null;
    const boll = indicators['BOLL'];
    if (!boll?.values?.length) return null;
    // 查找 multiPeriodValues 中的 BOLL key
    let bollKey: string | null = null;
    for (const pt of boll.values) {
        if (pt.multiPeriodValues) {
            for (const k of Object.keys(pt.multiPeriodValues)) {
                if (k.startsWith('boll_') && normalizeBollParam(k) === normalizeBollParam(requestedParam)) {
                    bollKey = k;
                    break;
                }
            }
        }
        if (bollKey) break;
    }
    if (null == bollKey) return null;
    const upper: LineData[] = [];
    const middle: LineData[] = [];
    const lower: LineData[] = [];
    for (const pt of boll.values) {
        const val = pt.multiPeriodValues?.[bollKey] as { upper?: number; middle?: number; lower?: number } | undefined;
        if (!val || null == val.middle) continue;
        const t = toUtcSeconds(pt.timestamp) as Time;
        if (null != val.upper) upper.push({time: t, value: val.upper});
        if (null != val.middle) middle.push({time: t, value: val.middle});
        if (null != val.lower) lower.push({time: t, value: val.lower});
    }
    if (0 === upper.length) return null;
    upper.sort((a, b) => (a.time as number) - (b.time as number));
    middle.sort((a, b) => (a.time as number) - (b.time as number));
    lower.sort((a, b) => (a.time as number) - (b.time as number));
    return {upper, middle, lower};
}

// ═══════════════════════════════
// 主组件
// ═══════════════════════════════

const PatternRecognitionPage: React.FC<{ readonlyIndicators?: boolean }> = ({readonlyIndicators = false}) => {
    const {selectedApiKey, setSelectedApiKey, isAuthenticated, isLoading: authLoading} = useAuth();
    const isPocPage = window.location.pathname.includes('/site/poc/pattern');
    const canUseAiAnalysis = !!isAuthenticated || isPocPage;

    // 状态
    // POC 页面强制 SWAP
    const [mode, setMode] = useState<'SPOT' | 'SWAP'>(() => {
        if (isPocPage) return 'SWAP';
        return loadConfig()?.mode as any ?? 'SWAP';
    });
    // URL 参数初始化（组合信号页跳转携带上下文）：优先级 URL > localStorage > 默认值
    const urlSymbol = new URLSearchParams(window.location.search).get('symbol');
    const urlInterval = new URLSearchParams(window.location.search).get('interval');
    const [symbol, setSymbol] = useState(() => {
        if (urlSymbol) return urlSymbol;
        return loadConfig()?.symbol ?? 'BTC-USDT-SWAP';
    });
    const [interval, setIntervalState] = useState(() => {
        const validIntervals: string[] = INTERVALS as unknown as string[];
        if (urlInterval && validIntervals.includes(urlInterval)) {
            if (isPocPage && '1H' !== urlInterval && '4H' !== urlInterval) return '1H';
            return urlInterval;
        }
        const cfg = loadConfig();
        if (cfg?.interval && validIntervals.includes(cfg.interval)) {
            // POC 模式只允许 1H/4H
            if (isPocPage && '1H' !== cfg.interval && '4H' !== cfg.interval) return '1H';
            return cfg.interval;
        }
        return isPocPage ? '1H' : '5m';
    });
    const [patterns, setPatterns] = useState<PatternDetectResult[]>([]);
    const [trendPatterns, setTrendPatterns] = useState<PatternDetectResult[]>([]);
    const [trendRequiredCandles, setTrendRequiredCandles] = useState<Record<string, number>>({});
    const [algorithmPatterns, setAlgorithmPatterns] = useState<PatternDetectResult[]>([]);
    const [selectedPatternIds, setSelectedPatternIds] = useState<Set<number>>(new Set());
    const [selectedTrendPatternIds, setSelectedTrendPatternIds] = useState<Set<number>>(new Set());
    const [selectedAlgorithmPatternId, setSelectedAlgorithmPatternId] = useState<number | null>(null);
    const [trendCategoryFilter, setTrendCategoryFilter] = useState<string>('ALL');
    const [selectedZoneIds, setSelectedZoneIds] = useState<Set<number>>(new Set());
    const [aiActive, setAiActive] = useState(true);
    const [activeTab, setActiveTab] = useState<'patterns' | 'zone' | 'trend' | 'algorithm' | 'ai' | 'history'>('patterns');
    const [latestCandle, setLatestCandle] = useState<{
        o: number;
        h: number;
        l: number;
        c: number;
        v: number;
        confirmedClose: number | null;
    } | null>(null);
    const [pageHeight, setPageHeight] = useState<number | null>(null);
    const [indicators, setIndicators] = useState<CompleteChartData['indicators']>(undefined);
    const [topNSymbols, setTopNSymbols] = useState<string[]>(buildSwapSymbols());
    const [backtest, setBacktest] = useState<BacktestStats | null>(null);
    const [zoneResults, setZoneResults] = useState<ZoneDetectResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    // 全局展示 API Key（POC 场景：用户未登录/无 Key 时用于数据访问权限豁免）
    const [globalApiKeyId, setGlobalApiKeyId] = useState<number | null>(null);
    // 计算当前生效的 apiKeyId：用户 Key > 全局展示 Key
    const effectiveApiKeyId = selectedApiKey ?? globalApiKeyId;
    const apiKeyIdRef = useRef<number | null>(effectiveApiKeyId);
    apiKeyIdRef.current = effectiveApiKeyId;
    // 全局展示 Key 的 ref（供行情请求失败时兜底重试，避免异步闭包陈旧）
    const globalApiKeyIdRef = useRef<number | null>(globalApiKeyId);
    globalApiKeyIdRef.current = globalApiKeyId;
    // AI 状态管理（reducer 模式）：统一管理形态/区域 AI 分析结果、加载态、展开态
    const [aiState, dispatch] = useReducer(patternAiReducer, undefined, createInitialAiState);
    const submissionUnresolved = ['submitting', 'processing', 'unknown', 'under_review']
        .includes(aiState.submissionStatus);
    const canTriggerAiAnalysis = canUseAiAnalysis && !submissionUnresolved;
    const [pendingOrderQueryLoading, setPendingOrderQueryLoading] = useState(false);
    const previousSubmissionStatusRef = useRef(aiState.submissionStatus);
    // 请求序号 ref：用于丢弃过期请求的响应（品种/信号切换时旧响应不覆盖新状态）
    const aiRequestSeqRef = useRef(0);
    const dataRequestSeqRef = useRef(0);
    const quickTradePreparingRef = useRef(false);
    // 能力开关 hook：根据认证状态拉取页面能力配置
    const authState = {ready: !authLoading, isAuthenticated: !!isAuthenticated, isPoc: isPocPage};
    usePatternCapabilities(dispatch, authState);

    // 从后端检测器元数据读取最低K线要求，避免前端维护一份容易过期的硬编码表。
    useEffect(() => {
        if (authLoading || (!isAuthenticated && !isPocPage)) return;
        let cancelled = false;
        getPatternList('TREND').then(items => {
            if (cancelled) return;
            setTrendRequiredCandles(Object.fromEntries(
                items
                    .filter(item => Number.isFinite(item.requiredCandleCount))
                    .map(item => [item.code, item.requiredCandleCount]),
            ));
        }).catch(() => {
            if (!cancelled) setTrendRequiredCandles({});
        });
        return () => {
            cancelled = true;
        };
    }, [authLoading, isAuthenticated, isPocPage]);

    // 拉取 API Key 列表供工具栏选择器使用。
    // selectedApiKey 由 TradingPage 管理全局状态，但若用户首次进入 pattern 页面
    // 尚未初始化时，自动选中第一个可用 key。
    useEffect(() => {
        if (authLoading || !isAuthenticated || isPocPage) return;
        let cancelled = false;
        getActiveApiKeys().then(response => {
            if (cancelled) return;
            const apiKeysData = response.data?.data ?? [];
            setApiKeys(apiKeysData);
            if (apiKeysData.length > 0 && null == selectedApiKey) {
                setSelectedApiKey(apiKeysData[0].keyId);
            }
        }).catch(() => {
            if (!cancelled) setApiKeys([]);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, isAuthenticated, isPocPage]);

    // 校验已存/已选的 API Key 是否仍在有效列表内：
    // localStorage 可能残留已删除/非本人的 Key，若不校验会持续用无效 key 拉行情 → 图表静默空白。
    useEffect(() => {
        if (authLoading || !isAuthenticated || isPocPage) return;
        if (0 === apiKeys.length) return;
        const hasSelected = null != selectedApiKey && apiKeys.some(k => k.keyId === selectedApiKey);
        if (hasSelected) return;
        // 未选择或已失效 → 回退到第一个有效 Key
        setSelectedApiKey(apiKeys[0].keyId);
    }, [apiKeys, selectedApiKey, authLoading, isAuthenticated, isPocPage, setSelectedApiKey]);

    // 图表 generation：图表重建时递增，用于 AI 叠加层重建恢复
    // Refs
    const pageRef = useRef<HTMLDivElement>(null);
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const [chartGeneration, setChartGeneration] = useState(0);
    const capabilities = selectCapabilities(aiState);
    const [indicatorConfigs, setIndicatorConfigs] = useState<IndicatorItemConfig[]>(() => loadConfig()?.indicatorConfigs ?? DEFAULT_INDICATOR_CONFIGS);
    const [expandedIndKey, setExpandedIndKey] = useState<string | null>(null);
    const [, setRelativeTimeTick] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => setRelativeTimeTick(value => value + 1), 60_000);
        return () => clearInterval(timer);
    }, []);

    // reducer 是提交状态的唯一数据源；sessionStorage 只保存只读恢复副本。
    // 刷新后只能按原幂等键查询，不能从该副本重新 POST。
    useEffect(() => {
        const snapshot = aiState.submissionSnapshot;
        if (!snapshot || !['submitting', 'processing', 'unknown', 'under_review']
            .includes(aiState.submissionStatus)) return;
        try {
            sessionStorage.setItem('pattern_ai_pending_order', JSON.stringify({
                snapshot,
                status: aiState.submissionStatus,
                message: aiState.submissionMessage,
                deadline: aiState.submissionPollingDeadline,
            }));
        } catch { /* 浏览器禁用存储时页面 reducer 仍继续跟踪 */
        }
    }, [aiState.submissionSnapshot, aiState.submissionStatus,
        aiState.submissionMessage, aiState.submissionPollingDeadline]);

    // 模式、品种或周期变化后，旧报告的交易上下文立即失效，绝不能提交旧信号。
    useEffect(() => {
        aiRequestSeqRef.current++;
        dataRequestSeqRef.current++;
        dispatch({type: 'RESET_CONTEXT'});
    }, [mode, symbol, interval]);

    // 提交状态属于页面级会话：抽屉关闭、切换 Tab 后继续使用原幂等键查询。
    useEffect(() => {
        if (aiState.submissionSnapshot) return;
        try {
            const stored = JSON.parse(sessionStorage.getItem('pattern_ai_pending_order') ?? 'null');
            if (stored?.snapshot?.idempotencyKey) {
                const storedDeadline = Number(stored.deadline);
                const pollingDeadline = Number.isFinite(storedDeadline) && 0 < storedDeadline
                    ? storedDeadline : Date.now() + 5 * 60 * 1000;
                dispatch({type: 'FREEZE_SUBMISSION', snapshot: stored.snapshot, pollingDeadline});
                dispatch({
                    type: 'SET_SUBMISSION_STATUS',
                    status: 'under_review' === stored.status ? 'under_review'
                        : 'unknown' === stored.status ? 'unknown' : 'processing',
                    message: stored.message ?? '正在恢复订单结果查询',
                });
            }
        } catch {
            sessionStorage.removeItem('pattern_ai_pending_order');
        }
    }, []);

    useEffect(() => {
        const snapshot = aiState.submissionSnapshot;
        const status = aiState.submissionStatus;
        if (!snapshot || !['submitting', 'processing', 'unknown', 'under_review'].includes(status)) return;
        let cancelled = false;
        let pollCount = 0;
        const pollingDeadline = aiState.submissionPollingDeadline;
        let timer: ReturnType<typeof setTimeout> | null = null;
        let requestInFlight = false;
        const poll = async () => {
            if (cancelled || requestInFlight) return;
            if ('under_review' !== status && null != pollingDeadline && Date.now() >= pollingDeadline) {
                dispatch({type: 'SET_SUBMISSION_STATUS', status: 'unknown', message: '自动查询已暂停，可手动继续查询'});
                return;
            }
            if ('hidden' === document.visibilityState) {
                timer = setTimeout(poll, 2000);
                return;
            }
            requestInFlight = true;
            try {
                const response = await api.get(`/trading/signal-orders/status/${snapshot.idempotencyKey}`);
                const data = response?.data?.data;
                if ('SUCCEEDED' === data?.status) {
                    dispatch({
                        type: 'SET_SUBMISSION_STATUS', status: 'succeeded',
                        message: formatSuccessfulOrderStatus(data)
                    });
                    sessionStorage.removeItem('pattern_ai_pending_order');
                    return;
                }
                if ('FAILED' === data?.status) {
                    dispatch({
                        type: 'SET_SUBMISSION_STATUS',
                        status: 'failed',
                        message: data?.message ?? '订单处理失败'
                    });
                    sessionStorage.removeItem('pattern_ai_pending_order');
                    return;
                }
                if (true === data?.manualRiskMode) {
                    dispatch({
                        type: 'SET_SUBMISSION_STATUS',
                        status: 'under_review',
                        message: data?.message ?? '订单仍在审核中'
                    });
                } else if ('submitting' === status || 'unknown' === status) {
                    dispatch({
                        type: 'SET_SUBMISSION_STATUS',
                        status: 'processing',
                        message: data?.message ?? '订单正在处理中'
                    });
                }
            } catch {
                if ('under_review' !== status && null != pollingDeadline && Date.now() >= pollingDeadline) {
                    dispatch({
                        type: 'SET_SUBMISSION_STATUS',
                        status: 'unknown',
                        message: '订单结果暂时未知，可稍后继续查询'
                    });
                }
            } finally {
                requestInFlight = false;
            }
            pollCount++;
            timer = setTimeout(poll, 'under_review' === status ? 15000 : pollCount < 10 ? 3000 : 15000);
        };
        const handleVisibilityChange = () => {
            if ('visible' !== document.visibilityState || cancelled || requestInFlight) return;
            if (timer) clearTimeout(timer);
            timer = setTimeout(poll, 0);
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        timer = setTimeout(poll, 2000);
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [aiState.submissionSnapshot?.idempotencyKey, aiState.submissionStatus,
        aiState.submissionPollingDeadline]);

    useEffect(() => {
        const previous = previousSubmissionStatusRef.current;
        previousSubmissionStatusRef.current = aiState.submissionStatus;
        if (aiState.tradePanelOpen
            || !['submitting', 'processing', 'unknown', 'under_review'].includes(previous)) return;
        if ('succeeded' === aiState.submissionStatus) {
            message.success(aiState.submissionMessage ?? '订单已成功提交');
        } else if ('failed' === aiState.submissionStatus) {
            message.error(aiState.submissionMessage ?? '订单处理失败');
        }
    }, [aiState.submissionStatus, aiState.submissionMessage, aiState.tradePanelOpen]);

    const queryPendingOrderStatus = async () => {
        const snapshot = aiState.submissionSnapshot;
        if (!snapshot || pendingOrderQueryLoading) return;
        setPendingOrderQueryLoading(true);
        try {
            const response = await api.get(`/trading/signal-orders/status/${snapshot.idempotencyKey}`);
            const statusData = response?.data?.data;
            if ('SUCCEEDED' === statusData?.status) {
                dispatch({
                    type: 'SET_SUBMISSION_STATUS', status: 'succeeded',
                    message: formatSuccessfulOrderStatus(statusData)
                });
                sessionStorage.removeItem('pattern_ai_pending_order');
            } else if ('FAILED' === statusData?.status) {
                dispatch({
                    type: 'SET_SUBMISSION_STATUS', status: 'failed',
                    message: statusData?.message ?? '订单处理失败'
                });
                sessionStorage.removeItem('pattern_ai_pending_order');
            } else if (true === statusData?.manualRiskMode) {
                dispatch({
                    type: 'SET_SUBMISSION_STATUS', status: 'under_review',
                    message: statusData?.message ?? '订单仍在审核中'
                });
            } else {
                dispatch({
                    type: 'SET_SUBMISSION_STATUS', status: 'processing',
                    message: statusData?.message ?? '订单仍在处理中'
                });
            }
        } catch {
            dispatch({
                type: 'SET_SUBMISSION_STATUS', status: 'unknown',
                message: '暂时无法查询订单结果，请稍后重试'
            });
        } finally {
            setPendingOrderQueryLoading(false);
        }
    };

    // 持久化配置到浏览器
    useEffect(() => {
        saveConfig({mode, symbol, interval, indicatorConfigs});
    }, [mode, symbol, interval, indicatorConfigs]);
    const [crosshair, setCrosshair] = useState<{
        time: number;
        open?: number;
        high?: number;
        low?: number;
        close?: number;
        volume?: number;
        x: number;
        y: number;
        chartW: number;
        indVals: Record<string, number | null>
    } | null>(null);
    const [realtimeQuote, setRealtimeQuote] = useState<{
        symbol: string;
        price: number;
        change24h: number | null;
    } | null>(null);
    const [activeViewId, setActiveViewId] = useState<{
        type: 'pattern' | 'zone' | 'trend' | 'algorithm';
        id: number
    } | null>(null);
    const rtPrice = realtimeQuote?.symbol === symbol ? realtimeQuote.price : null;
    const rtChange24h = realtimeQuote?.symbol === symbol ? realtimeQuote.change24h : null;

    const resolveSignalExpiryContext = (key: AnalysisKey): {
        signalFormedAt: number;
        zoneType?: string;
        zoneUpper?: number;
        zoneLower?: number;
    } | null => {
        const id = Number(key.split(':')[1]);
        if (key.startsWith('ZONE:')) {
            const zone = zoneResults.find(item => item.id === id);
            return zone ? {
                signalFormedAt: zone.zoneEndTime,
                zoneType: zone.zoneType,
                zoneUpper: Number(zone.zoneUpper),
                zoneLower: Number(zone.zoneLower),
            } : null;
        }
        if (key.startsWith('TREND:')) {
            const trend = trendPatterns.find(item => item.id === id);
            return trend ? {signalFormedAt: trend.klineEndTime} : null;
        }
        if (key.startsWith('ALGORITHM:')) {
            const algo = algorithmPatterns.find(item => item.id === id);
            return algo ? {signalFormedAt: algo.klineEndTime} : null;
        }
        const pattern = patterns.find(item => item.id === id);
        return pattern ? {signalFormedAt: pattern.klineEndTime} : null;
    };
    const expandedAnalysis = null == aiState.expandedAnalysisKey
        ? null : aiState.analysisByKey[aiState.expandedAnalysisKey] ?? null;
    const expandedSignalContext = null == aiState.expandedAnalysisKey
        ? null : resolveSignalExpiryContext(aiState.expandedAnalysisKey);
    const expandedExpiry = useSignalExpiry(
        expandedAnalysis && expandedSignalContext ? {
            currentPrice: rtPrice ?? latestCandle?.c ?? 0,
            entryPrice: expandedAnalysis.entryPrice ?? 0,
            expectedHoldingUntil: expandedAnalysis.expectedHoldingUntil,
            signalFormedAt: expandedSignalContext.signalFormedAt,
            interval,
            zoneType: expandedSignalContext.zoneType,
            zoneUpper: expandedSignalContext.zoneUpper,
            zoneLower: expandedSignalContext.zoneLower,
            latestClose: latestCandle?.confirmedClose ?? undefined,
            policy: capabilities,
        } : null,
        capabilities.overlayEnabled || capabilities.quickTradeEnabled,
    );
    const intervalMs = useMemo(() => {
        const map: Record<string, number> = {'1m': 60000, '5m': 300000, '1H': 3600000, '4H': 14400000, '1D': 86400000};
        return map[interval] ?? 3600000;
    }, [interval]);
    // 收集所有需要展示AI overlay的信号key：
    // 1. 已选中的形态/区域（通过卡片点击切换）
    // 2. 当前展开的AI分析（通过AI按钮触发，无论卡片是否选中）
    const selectedAnalysisKeys = useMemo(() => {
        const keys: import('./pattern/types').AnalysisKey[] = [];
        for (const pid of selectedPatternIds) {
            const key = ('PATTERN:' + pid) as import('./pattern/types').AnalysisKey;
            if (aiState.analysisByKey[key]) keys.push(key);
        }
        for (const zid of selectedZoneIds) {
            const key = ('ZONE:' + zid) as import('./pattern/types').AnalysisKey;
            if (aiState.analysisByKey[key]) keys.push(key);
        }
        for (const tid of selectedTrendPatternIds) {
            const key = ('TREND:' + tid) as import('./pattern/types').AnalysisKey;
            if (aiState.analysisByKey[key]) keys.push(key);
        }
        if (null != selectedAlgorithmPatternId) {
            const key = ('ALGORITHM:' + selectedAlgorithmPatternId) as import('./pattern/types').AnalysisKey;
            if (aiState.analysisByKey[key]) keys.push(key);
        }
        if (aiState.expandedAnalysisKey && aiState.analysisByKey[aiState.expandedAnalysisKey]) {
            if (!keys.includes(aiState.expandedAnalysisKey)) {
                keys.push(aiState.expandedAnalysisKey);
            }
        }
        return keys;
    }, [selectedPatternIds, selectedZoneIds, selectedTrendPatternIds, selectedAlgorithmPatternId, aiState.analysisByKey, aiState.expandedAnalysisKey]);

    // 为每个信号生成formationTime映射
    const signalFormationTimes = useMemo(() => {
        const map = new Map<string, number>();
        for (const pid of selectedPatternIds) {
            const pat = patterns.find(p => p.id === pid);
            if (pat) map.set('PATTERN:' + pid, pat.klineEndTime);
        }
        for (const zid of selectedZoneIds) {
            const zone = zoneResults.find(z => z.id === zid);
            if (zone) map.set('ZONE:' + zid, zone.zoneEndTime);
        }
        for (const tid of selectedTrendPatternIds) {
            const tp = trendPatterns.find(t => t.id === tid);
            if (tp) map.set('TREND:' + tid, tp.klineEndTime);
        }
        if (aiState.expandedAnalysisKey && !map.has(aiState.expandedAnalysisKey)) {
            const id = Number(aiState.expandedAnalysisKey.split(':')[1]);
            if (aiState.expandedAnalysisKey.startsWith('PATTERN:')) {
                const pat = patterns.find(p => p.id === id);
                if (pat) map.set(aiState.expandedAnalysisKey, pat.klineEndTime);
            } else if (aiState.expandedAnalysisKey.startsWith('TREND:')) {
                const tp = trendPatterns.find(t => t.id === id);
                if (tp) map.set(aiState.expandedAnalysisKey, tp.klineEndTime);
            } else if (aiState.expandedAnalysisKey.startsWith('ALGORITHM:')) {
                const ap = algorithmPatterns.find(a => a.id === id);
                if (ap) map.set(aiState.expandedAnalysisKey, ap.klineEndTime);
            } else {
                const zone = zoneResults.find(z => z.id === id);
                if (zone) map.set(aiState.expandedAnalysisKey, zone.zoneEndTime);
            }
        }
        return map;
    }, [selectedPatternIds, selectedZoneIds, selectedTrendPatternIds, patterns, zoneResults, trendPatterns, aiState.expandedAnalysisKey]);

    const rawPlans = useMemo(() =>
            selectActiveTradePlans(aiState, selectedAnalysisKeys, intervalMs, signalFormationTimes),
        [
            aiState.capabilities.overlayEnabled,
            aiState.overlayVisible,
            aiState.analysisByKey,
            selectedAnalysisKeys,
            intervalMs,
            signalFormationTimes,
        ],
    );

    useMultiAiTradePlanOverlay(chartRef, rawPlans, chartGeneration, capabilities.overlayEnabled, chartContainerRef);

    const centerOnSignal = (start: number, end: number) => {
        const chart = chartRef.current;
        if (!chart) return;
        const startMs = timestampMillis(start);
        const endMs = timestampMillis(end);
        const centerMs = (startMs + endMs) / 2;
        const intervalMs = intervalMilliseconds(interval);
        const paddingMs = 5 * intervalMs; // 每侧至少5根K线宽度的留白
        const minRangeMs = intervalMs * 30;
        const rangeMs = Math.max(endMs - startMs + 2 * paddingMs, minRangeMs);
        const halfRange = rangeMs / 2;
        chart.timeScale().setVisibleRange({
            from: Math.floor((centerMs - halfRange) / 1000) as Time,
            to: Math.floor((centerMs + halfRange) / 1000) as Time,
        });
    };

    const handleViewChartToggle = (type: 'pattern' | 'zone' | 'trend' | 'algorithm', id: number, start: number, end: number) => {
        // 确保该item被选中（已选中的保持不变，未选中的加入选中）
        if ('pattern' === type) {
            setSelectedPatternIds(prev => {
                if (prev.has(id)) return prev;
                if (prev.size >= MAX_SELECTION) {
                    message.warning('最多同时选择' + MAX_SELECTION + '个形态');
                    return prev;
                }
                const next = new Set(prev);
                next.add(id);
                return next;
            });
        } else if ('zone' === type) {
            setSelectedZoneIds(prev => {
                if (prev.has(id)) return prev;
                if (prev.size >= MAX_SELECTION) {
                    message.warning('最多同时选择' + MAX_SELECTION + '个区域');
                    return prev;
                }
                const next = new Set(prev);
                next.add(id);
                return next;
            });
        } else if ('trend' === type) {
            setSelectedTrendPatternIds(prev => {
                if (prev.has(id)) return prev;
                if (prev.size >= MAX_SELECTION) {
                    message.warning('最多同时选择' + MAX_SELECTION + '个趋势');
                    return prev;
                }
                const next = new Set(prev);
                next.add(id);
                return next;
            });
        } else {
            setSelectedAlgorithmPatternId(prev => prev === id ? null : id);
        }
        if (activeViewId?.type === type && activeViewId?.id === id) {
            setActiveViewId(null);
            const chart = chartRef.current;
            if (chart) chart.timeScale().fitContent();
        } else {
            setActiveViewId({type, id});
            centerOnSignal(start, end);
        }
    };

    const evaluateAnalysisExpiry = (key: AnalysisKey, analysis: AIAnalysis) => {
        const context = resolveSignalExpiryContext(key);
        if (null == context) return {expired: true, reason: '无法获取信号形成时间'};
        return checkAllExpiry({
            currentPrice: rtPrice ?? latestCandle?.c ?? 0,
            entryPrice: analysis.entryPrice ?? 0,
            expectedHoldingUntil: analysis.expectedHoldingUntil,
            signalFormedAt: context.signalFormedAt,
            interval,
            zoneType: context.zoneType,
            zoneUpper: context.zoneUpper,
            zoneLower: context.zoneLower,
            latestClose: latestCandle?.confirmedClose ?? undefined,
            policy: capabilities,
        });
    };

    // 与 Trading 页 QuickTradingPanel 使用完全相同的普通下单服务和请求结构。
    // 形态页只负责在提交前自动算出 5–50U 对应的 allocationPercent。
    const executeDirectQuickTrade = async (orderRequest: OrderRequest) => {
        try {
            const response = await tradingService.placeOrder(orderRequest);
            const result = response.data;
            if (result.success) {
                const orderId = result.data?.orderId || (result as any).orderId;
                const manualRiskMode = true === (result.data as any)?.manualRiskMode
                    || true === result.data?.isManualRiskMode;
                if (manualRiskMode) {
                    const text = `订单已提交审核${orderId ? ` · 审核单ID: ${orderId}` : ''}`;
                    message.info(text);
                } else {
                    const text = `下单成功${orderId ? ` · 订单ID: ${orderId}` : ''}`;
                    message.success(text);
                }
                return;
            }
            throw new Error(result.message || '未知错误');
        } catch (error: any) {
            if (401 === error.response?.status) throw new Error('认证失败，请检查API密钥配置');
            if (403 === error.response?.status) throw new Error('权限不足，请检查API密钥权限');
            throw new Error(error.response?.data?.message || error.message || '请稍后重试');
        }
    };

    const openAiTradePanel = async (source: {
        key: AnalysisKey;
        analysis: AIAnalysis;
        signalDirection: 1 | -1 | 0;
        signalConfidence?: number;
        tradeIntent: 'FORWARD' | 'REVERSE';
    }) => {
        if (submissionUnresolved) {
            message.warning('当前订单结果尚未确认，请先查询原订单结果');
            return;
        }
        if ('SPOT' === mode) {
            message.warning('AI 快捷交易当前仅支持永续合约');
            return;
        }
        if (!canOpenAiTradeDraft(source.analysis, source.signalDirection)) {
            message.warning('AI 价格关系与信号方向不一致，不能快捷下单');
            return;
        }
        if (hasMissingAiPrices(source.analysis)) {
            message.warning('AI 交易价格信息不完整，无法快捷下单');
            return;
        }
        const expiry = evaluateAnalysisExpiry(source.key, source.analysis);
        if (expiry.expired) {
            message.warning(`信号已过期：${expiry.reason ?? '当前交易计划不再有效'}`);
            return;
        }
        if (quickTradePreparingRef.current) return;
        if (!selectedApiKey) {
            message.warning('请先选择 API Key');
            return;
        }
        const draftKey = 0 === source.signalDirection
            ? ('FORWARD' === source.tradeIntent ? 'MANUAL_LONG' : 'MANUAL_SHORT')
            : source.tradeIntent;
        const drafts = createTradeDrafts(
            source.signalDirection,
            source.signalConfidence,
            source.analysis.entryPrice!,
            source.analysis.targetPrice!,
            source.analysis.stopLossPrice!,
        );
        const rawDraft = drafts[draftKey] as SignalTradeDraft | undefined;
        if (!rawDraft) {
            message.error('无法生成当前方向的交易计划');
            return;
        }

        quickTradePreparingRef.current = true;
        message.loading({content: '正在计算订单预览...', key: 'pattern-quick-trade', duration: 0});
        try {
            const instrumentSpec: any = await tradingService.getInstrumentSpec(symbol, selectedApiKey);
            const tickSz = Number(instrumentSpec?.tickSz);
            if (!Number.isFinite(tickSz) || 0 >= tickSz) {
                throw new Error('无法获取合约最小价格变动单位');
            }
            const draft = normalizeTradeDraftPrices(rawDraft, tickSz);
            const priceError = validateTradePlanPrices(draft);
            if (priceError) throw new Error(priceError);

            const basePreviewRequest = {
                apiKeyId: selectedApiKey,
                instId: symbol,
                side: 'long' === draft.posSide ? 'buy' : 'sell',
                posSide: draft.posSide,
                orderType: draft.orderType,
                lever: draft.leverage,
                px: draft.entryPrice,
                takeProfitPrice: draft.targetPrice,
                stopLossPrice: draft.stopLossPrice,
                source: 'web',
            } as const;

            let allocationPercent = 5;
            const previewResponse: any = await tradingService.previewOrderSizing({
                ...basePreviewRequest,
                allocationPercent,
                quickTradePreview: true,
            });
            const previewData = previewResponse?.data?.data;
            if (!previewResponse?.data?.success || !previewData) {
                throw new Error(previewResponse?.data?.message || '无法计算订单预览');
            }

            allocationPercent = Number(previewData.requestedAllocationPercent);
            if (!Number.isFinite(allocationPercent) || allocationPercent <= 0 || allocationPercent > 25) {
                throw new Error('无法获得有效的快捷交易开仓比例');
            }

            const actualMargin = Number(previewData.actualMargin);
            const estimatedQuantity = Number(previewData.size ?? previewData.orderSize);
            if (!Number.isFinite(actualMargin) || actualMargin < 5 || actualMargin > 50) {
                throw new Error('无法将保证金控制在 5–50 USDT 范围内');
            }
            if (!Number.isFinite(estimatedQuantity) || estimatedQuantity <= 0) {
                throw new Error('无法获得有效的预计下单数量');
            }

            const orderRequest: OrderRequest = {
                apiKeyId: selectedApiKey,
                instId: symbol,
                side: 'long' === draft.posSide ? 'buy' : 'sell',
                posSide: draft.posSide,
                orderType: draft.orderType,
                amount: 0, // 快交面板走 allocationPercent 计算，amount 由后端按比例解析
                allocationPercent,
                lever: draft.leverage,
                px: draft.entryPrice,
                takeProfitPrice: draft.targetPrice,
                stopLossPrice: draft.stopLossPrice,
                source: 'web',
            };
            const isLiveTrading = apiKeys.find(key => key.keyId === selectedApiKey)?.isLiveTrading ?? false;
            const displayedAllocationPercent = Number(previewData.actualAllocationPercent);

            message.destroy('pattern-quick-trade');
            showTradingConfirmDialog({
                direction: draft.posSide,
                instId: symbol,
                orderType: draft.orderType,
                limitPrice: draft.entryPrice,
                currentPrice: Number(previewData.estimatedExecutionPrice) || draft.entryPrice,
                allocationPercent: Number.isFinite(displayedAllocationPercent)
                    ? displayedAllocationPercent : allocationPercent,
                lever: draft.leverage,
                estimatedMargin: actualMargin,
                estimatedQuantity,
                notional: actualMargin * draft.leverage,
                takeProfitPrice: draft.targetPrice,
                stopLossPrice: draft.stopLossPrice,
                isLiveTrading,
                onConfirm: () => executeDirectQuickTrade(orderRequest),
            });
        } catch (error: any) {
            message.error({
                content: error?.response?.data?.message || error?.message || '订单预览失败',
                key: 'pattern-quick-trade',
            });
        } finally {
            quickTradePreparingRef.current = false;
        }
    };

    const handlePatternAiClick = async (pattern: PatternDetectResult, key: AnalysisKey) => {
        const current = aiState.analysisByKey[key];
        if (aiState.analysisRequestByKey[key]?.status === 'loading' || !canTriggerAiAnalysis) return;
        if (current) {
            dispatch(aiState.expandedAnalysisKey === key
                ? {type: 'COLLAPSE_ANALYSIS'}
                : {type: 'EXPAND_ANALYSIS', key});
            return;
        }
        dispatch({type: 'EXPAND_ANALYSIS', key});
        const requestSeq = ++aiRequestSeqRef.current;
        dispatch({type: 'AI_REQUEST_STARTED', key, requestSeq});
        try {
            const result = pattern.hasAiAnalysis
                ? await getAIAnalysis(pattern.id)
                : await triggerAIAnalysis(pattern.id);
            if (result && result.patternResultId === pattern.id) {
                dispatch({
                    type: 'AI_REQUEST_SUCCEEDED', key, requestSeq,
                    analysis: {
                        ...result, signalDirection: pattern.direction as 1 | -1 | 0,
                        signalConfidence: pattern.confidence
                    },
                });
            } else {
                if (result) console.error('AI analysis protocol mismatch', {
                    expectedPatternResultId: pattern.id,
                    actualPatternResultId: result.patternResultId,
                });
                dispatch({
                    type: 'AI_REQUEST_FAILED', key, requestSeq,
                    error: result ? 'AI分析响应关联错误，请刷新后重试' : '未获取到AI分析结果，请稍后重试'
                });
            }
        } catch {
            dispatch({type: 'AI_REQUEST_FAILED', key, requestSeq, error: 'AI分析失败，请稍后重试'});
        }
    };

    const handleZoneAiClick = async (zone: ZoneDetectResult, key: AnalysisKey) => {
        const current = aiState.analysisByKey[key];
        if (aiState.analysisRequestByKey[key]?.status === 'loading' || !canTriggerAiAnalysis) return;
        if (current) {
            dispatch(aiState.expandedAnalysisKey === key
                ? {type: 'COLLAPSE_ANALYSIS'}
                : {type: 'EXPAND_ANALYSIS', key});
            return;
        }
        dispatch({type: 'EXPAND_ANALYSIS', key});
        const requestSeq = ++aiRequestSeqRef.current;
        dispatch({type: 'AI_REQUEST_STARTED', key, requestSeq});
        try {
            const result = zone.hasAiAnalysis
                ? await getZoneAIAnalysis(zone.id)
                : await triggerZoneAIAnalysis(zone.id);
            if (result && result.zoneResultId === zone.id) {
                dispatch({
                    type: 'AI_REQUEST_SUCCEEDED', key, requestSeq,
                    analysis: {
                        ...result, signalDirection: zone.direction as 1 | -1 | 0,
                        signalConfidence: zone.confidence
                    },
                });
            } else {
                if (result) console.error('Zone AI analysis protocol mismatch', {
                    expectedZoneResultId: zone.id,
                    actualZoneResultId: result.zoneResultId,
                });
                dispatch({
                    type: 'AI_REQUEST_FAILED', key, requestSeq,
                    error: result ? '区域AI分析响应关联错误，请刷新后重试' : '未获取到区域AI分析结果，请稍后重试'
                });
            }
        } catch {
            dispatch({type: 'AI_REQUEST_FAILED', key, requestSeq, error: '区域AI分析失败，请稍后重试'});
        }
    };
    const handleTrendAiClick = async (pattern: PatternDetectResult, key: AnalysisKey) => {
        const current = aiState.analysisByKey[key];
        if (aiState.analysisRequestByKey[key]?.status === 'loading' || !canTriggerAiAnalysis) return;
        if (current) {
            dispatch(aiState.expandedAnalysisKey === key
                ? {type: 'COLLAPSE_ANALYSIS'}
                : {type: 'EXPAND_ANALYSIS', key});
            return;
        }
        dispatch({type: 'EXPAND_ANALYSIS', key});
        const requestSeq = ++aiRequestSeqRef.current;
        dispatch({type: 'AI_REQUEST_STARTED', key, requestSeq});
        try {
            const result = pattern.hasAiAnalysis
                ? await getAIAnalysis(pattern.id)
                : await triggerAIAnalysis(pattern.id);
            if (result && result.patternResultId === pattern.id) {
                dispatch({
                    type: 'AI_REQUEST_SUCCEEDED', key, requestSeq,
                    analysis: {
                        ...result, signalDirection: pattern.direction as 1 | -1 | 0,
                        signalConfidence: pattern.confidence
                    },
                });
            } else {
                if (result) console.error('Trend AI analysis protocol mismatch', {
                    expectedPatternResultId: pattern.id,
                    actualPatternResultId: result.patternResultId,
                });
                dispatch({
                    type: 'AI_REQUEST_FAILED', key, requestSeq,
                    error: result ? '趋势AI分析响应关联错误，请刷新后重试' : '未获取到趋势AI分析结果，请稍后重试'
                });
            }
        } catch {
            dispatch({type: 'AI_REQUEST_FAILED', key, requestSeq, error: '趋势AI分析失败，请稍后重试'});
        }
    };
    const handleAlgorithmAiClick = async (pattern: PatternDetectResult, key: AnalysisKey) => {
        const current = aiState.analysisByKey[key];
        if (aiState.analysisRequestByKey[key]?.status === 'loading' || !canTriggerAiAnalysis) return;
        if (current) {
            dispatch(aiState.expandedAnalysisKey === key
                ? {type: 'COLLAPSE_ANALYSIS'}
                : {type: 'EXPAND_ANALYSIS', key});
            return;
        }
        dispatch({type: 'EXPAND_ANALYSIS', key});
        const requestSeq = ++aiRequestSeqRef.current;
        dispatch({type: 'AI_REQUEST_STARTED', key, requestSeq});
        try {
            const result = pattern.hasAiAnalysis
                ? await getAIAnalysis(pattern.id)
                : await triggerAIAnalysis(pattern.id);
            if (result && result.patternResultId === pattern.id) {
                dispatch({
                    type: 'AI_REQUEST_SUCCEEDED', key, requestSeq,
                    analysis: {
                        ...result, signalDirection: pattern.direction as 1 | -1 | 0,
                        signalConfidence: pattern.confidence
                    },
                });
            } else {
                if (result) console.error('Algorithm AI analysis protocol mismatch', {
                    expectedPatternResultId: pattern.id,
                    actualPatternResultId: result.patternResultId,
                });
                dispatch({
                    type: 'AI_REQUEST_FAILED', key, requestSeq,
                    error: result ? '算法AI分析响应关联错误，请刷新后重试' : '未获取到算法AI分析结果，请稍后重试'
                });
            }
        } catch {
            dispatch({type: 'AI_REQUEST_FAILED', key, requestSeq, error: '算法AI分析失败，请稍后重试'});
        }
    };

    const realtimeRequestSeqRef = useRef(0);
    const klineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastKlineRefreshRef = useRef<number>(Date.now());
    const chartTimeRef = useRef<{ start: number; end: number } | null>(null);

    // 计算区域有效结束时间：检查区域结束到最新之间的K线，若有突破则不延伸
    function getZoneEndTime(z: ZoneDetectResult): number {
        const completedCandles = rawCandles.filter(candle => 1 === Number(candle.confirm));
        if (0 === completedCandles.length) return z.zoneEndTime;
        const zonePrice = Number(z.zonePrice);
        const isSupport = 'LOW' === z.zoneType;
        const isResistance = 'HIGH' === z.zoneType;
        const isRange = 'RANGE' === z.zoneType;
        // 从区域结束后第一根K线开始检查，有任何突破则终止延伸
        for (let ci = completedCandles.length - 1; ci >= 0; ci--) {
            const c = completedCandles[ci];
            if (c.timestamp <= z.zoneEndTime) break;
            if (isSupport && c.close <= zonePrice) return z.zoneEndTime;
            if (isResistance && c.close >= zonePrice) return z.zoneEndTime;
            if (isRange && (c.close >= Number(z.zoneUpper) || c.close <= Number(z.zoneLower))) {
                return z.zoneEndTime;
            }
        }
        // 区间内无任何突破，延伸至最新K线
        return completedCandles[completedCandles.length - 1].timestamp;
    }

    // ═══════════════ 精确测量可用高度 ═══════════════
    // 绕过外层 Antd Layout min-height:100vh 导致的 flex stretch 失效问题
    useEffect(() => {
        const el = pageRef.current;
        if (!el) return;
        const calc = () => {
            const top = el.getBoundingClientRect().top;
            setPageHeight(window.innerHeight - top);
        };
        calc();
        const ro = new ResizeObserver(calc);
        ro.observe(el);
        window.addEventListener('resize', calc);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', calc);
        };
    }, []);

    // 选中形态（用于回测tab，取第一个选中的形态）
    const selected = patterns.find(p => selectedPatternIds.has(p.id)) ?? null;

    // ═══════════════ 加载形态 + OHLC数据 ═══════════════
    const [rawCandles, setRawCandles] = useState<MarkPriceCandle[]>([]);
    const [chartDataIdentity, setChartDataIdentity] = useState<string | null>(null);
    /** K线加载错误（失败/为空时展示，避免图表静默空白） */
    const [chartError, setChartError] = useState<string | null>(null);
    const chartRequestSeqRef = useRef(0);

    // 从 indicatorConfigs 提取 API 请求参数（ref 避免 fetchOHLC 闭包陈旧）
    const indicatorParamsRef = useRef<{
        indicators: string[];
        smaPeriods: number[];
        bollParams: string[];
        rsiPeriods: number[];
        macdPeriods: number[]
    }>({
        indicators: ['RSI', 'MACD', 'BOLL', 'SMA'],
        smaPeriods: [7, 25],
        bollParams: ['20_2.0'],
        rsiPeriods: [14],
        macdPeriods: [12, 26, 9]
    });

    const fetchOHLC = useCallback(async (preferredApiKeyId?: number | null) => {
        const requestSymbol = symbol;
        const requestInterval = interval;
        const requestIdentity = `${requestSymbol}|${requestInterval}`;
        const requestSeq = ++chartRequestSeqRef.current;

        // 应用一次成功响应；序号/品种/周期不匹配时丢弃
        const applyData = (data: CompleteChartData | null | undefined): boolean => {
            if (requestSeq !== chartRequestSeqRef.current) return false;
            if (data?.instId && data.instId !== requestSymbol) return false;
            if (data?.timeframe && data.timeframe !== requestInterval) return false;
            setChartDataIdentity(requestIdentity);
            if (data?.candles?.length) {
                const sorted = [...data.candles].sort((a, b) => a.timestamp - b.timestamp);
                // 同步写入 chartTimeRef，确保后续 loadData 能获取到可视范围
                chartTimeRef.current = {
                    start: sorted[0].timestamp,
                    end: sorted[sorted.length - 1].timestamp,
                };
                const lastConfirmed = [...sorted].reverse().find(candle => 1 === Number(candle.confirm));
                setRawCandles(sorted);
                setIndicators(data.indicators);
                const last = sorted[sorted.length - 1];
                setLatestCandle({
                    o: Number(last.open), h: Number(last.high), l: Number(last.low),
                    c: Number(last.close), v: Number(last.volume ?? 0),
                    confirmedClose: lastConfirmed ? Number(lastConfirmed.close) : null,
                });
                setChartError(null);
            } else {
                setRawCandles([]);
                setIndicators(undefined);
                setLatestCandle(null);
                setChartError('暂无K线数据');
            }
            return true;
        };

        const request = (apiKeyId?: number) => tradingService.getCompleteChartData({
            instId: requestSymbol,
            apiKeyId,
            timeframe: requestInterval,
            limit: 240,
            indicators: indicatorParamsRef.current.indicators,
            ...(indicatorParamsRef.current.smaPeriods.length > 0 && {smaPeriods: indicatorParamsRef.current.smaPeriods}),
            ...(indicatorParamsRef.current.bollParams.length > 0 && {bollParams: indicatorParamsRef.current.bollParams}),
            ...(indicatorParamsRef.current.rsiPeriods.length > 0 && {rsiPeriods: indicatorParamsRef.current.rsiPeriods}),
            ...(indicatorParamsRef.current.macdPeriods.length > 0 && {macdPeriods: indicatorParamsRef.current.macdPeriods}),
        });

        // POC 未登录且全局展示 Key 尚未就绪：跳过请求，待 key 就绪后由依赖变化自动重试，避免无谓的 403 报错
        if (null == selectedApiKey && null == globalApiKeyIdRef.current && !isAuthenticated && null == preferredApiKeyId) {
            return;
        }

        const usedKey = preferredApiKeyId ?? apiKeyIdRef.current ?? undefined;
        try {
            applyData(await request(usedKey));
        } catch (e: any) {
            if (requestSeq !== chartRequestSeqRef.current) return;
            const msg = e?.message ?? 'K线数据加载失败';
            // 当前 Key 无权限/已失效 → 取全局展示 Key 兜底重试一次（避免图表静默空白）
            const isPermissionError = /权限|API Key|未登录|未授权|forbidden/i.test(msg);
            let fallbackKey = globalApiKeyIdRef.current;
            if (isPermissionError && null == fallbackKey && !isPocPage) {
                try {
                    const conn = await apiConnectionService.getGlobalDisplayConnection();
                    fallbackKey = conn?.apiKeyId ?? null;
                    if (null != fallbackKey) setGlobalApiKeyId(fallbackKey);
                } catch {
                    fallbackKey = null;
                }
            }
            if (isPermissionError && null != fallbackKey && fallbackKey !== usedKey) {
                try {
                    applyData(await request(fallbackKey));
                    return;
                } catch (e2: any) {
                    const fallbackMsg = e2?.message ?? 'K线数据加载失败';
                    setChartError(fallbackMsg);
                    message.error(fallbackMsg);
                    return;
                }
            }
            setChartError(msg);
            message.error(msg);
        }
    }, [symbol, interval, selectedApiKey, globalApiKeyId, isAuthenticated, isPocPage]);

    useEffect(() => {
        setActiveViewId(null);
    }, [symbol, interval]);

    // 同步图表K线时间范围到 ref，供 loadData 使用
    useEffect(() => {
        if (rawCandles.length > 0) {
            chartTimeRef.current = {
                start: rawCandles[0].timestamp,
                end: rawCandles[rawCandles.length - 1].timestamp,
            };
        }
    }, [rawCandles]);

    // 获取 topN 品种列表
    useEffect(() => {
        if (null == selectedApiKey) {
            setTopNSymbols(buildSwapSymbols());
            return;
        }
        import('../../services/tradingService').then(({getTopNInstruments}) => {
            getTopNInstruments(10, selectedApiKey).then(list => {
                if (list?.length) {
                    const ids = 'SWAP' === mode
                        ? list.map(i => i.instId)
                        : list.map(i => i.instId.replace(/-SWAP$/, ''));
                    setTopNSymbols(ids);
                    // URL 带入的跳转品种优先保留：追加进选项而非被 topN 重置
                    if (urlSymbol && !ids.includes(urlSymbol)) {
                        setTopNSymbols(prev => [urlSymbol, ...prev]);
                    } else if (!ids.includes(symbol)) {
                        setSymbol(ids[0]);
                    }
                }
            }).catch(() => {
                /* TopN 加载失败时保留内置品种列表。 */
            });
        });
    }, [selectedApiKey, mode]);

    const loadData = useCallback(async () => {
        // POC 未登录用户等待全局展示Key就绪后再请求数据
        if (null == selectedApiKey && null == globalApiKeyId) return;
        const requestSeq = ++dataRequestSeqRef.current;
        const requestIdentity = `${symbol}|${interval}`;
        const tr = chartTimeRef.current;
        const [candleResponse, zoneResponse, trendResponse, algorithmResponse] = await Promise.allSettled([
            getPatternResults(symbol, interval, 30, tr?.start, tr?.end, 'CANDLE', apiKeyIdRef.current ?? undefined),
            getZoneResults(symbol, interval, tr?.start, tr?.end, apiKeyIdRef.current ?? undefined),
            getPatternResults(symbol, interval, 30, tr?.start, tr?.end, 'TREND', apiKeyIdRef.current ?? undefined),
            // 算法形态的确认时间可能早于当前图表可视窗口；列表不能因图表缩放而为空。
            // 选中 Item 后再由 rawData.windowStartTime/windowEndTime 定位图表。
            getPatternResults(symbol, interval, 50, undefined, undefined, 'ALGORITHM', apiKeyIdRef.current ?? undefined),
        ]);
        if (requestSeq !== dataRequestSeqRef.current
            || requestIdentity !== `${symbol}|${interval}`) return;
        const patternResults = candleResponse.status === 'fulfilled' ? candleResponse.value : [];
        const zones = zoneResponse.status === 'fulfilled' ? zoneResponse.value : [];
        const trendResults = trendResponse.status === 'fulfilled' ? trendResponse.value : [];
        const algorithmResults = algorithmResponse.status === 'fulfilled' ? algorithmResponse.value : [];
        if (algorithmResponse.status === 'rejected') {
            console.error('加载算法形态失败', algorithmResponse.reason);
            message.error('算法形态加载失败，请点击“立即检测”重试');
        }
        if (candleResponse.status === 'rejected' || zoneResponse.status === 'rejected' || trendResponse.status === 'rejected') {
            message.error('部分形态数据加载失败');
        }
        setPatterns(patternResults);
        setTrendPatterns(trendResults);
        setAlgorithmPatterns(algorithmResults);
        setZoneResults(zones);
        // 清除已不在列表中的选中ID
        setSelectedPatternIds(prev => {
            const validIds = new Set(patternResults.map(p => p.id));
            const next = new Set(prev);
            for (const id of prev) {
                if (!validIds.has(id)) next.delete(id);
            }
            return next;
        });
        setSelectedTrendPatternIds(prev => {
            const validIds = new Set(trendResults.map(p => p.id));
            const next = new Set(prev);
            for (const id of prev) {
                if (!validIds.has(id)) next.delete(id);
            }
            return next;
        });
        setSelectedAlgorithmPatternId(prev => algorithmResults.some(item => item.id === prev) ? prev : null);
        setSelectedZoneIds(prev => {
            const validIds = new Set(zones.map(z => z.id));
            const next = new Set(prev);
            for (const id of prev) {
                if (!validIds.has(id)) next.delete(id);
            }
            return next;
        });
    }, [symbol, interval, globalApiKeyId]);

    // 获取全局展示 API Key（当用户未登录/无 Key 时，用于 POC 页面权限豁免）
    useEffect(() => {
        if (null != selectedApiKey) return;
        apiConnectionService.getGlobalDisplayConnection()
            .then((conn: { apiKeyId: number | null; exchange: string | null }) => {
                if (conn?.apiKeyId) setGlobalApiKeyId(conn.apiKeyId);
            })
            .catch(() => {
            });
    }, [selectedApiKey]);

    // 全局展示Key就绪后触发首次数据加载（POC场景，解决竞态条件）
    useEffect(() => {
        if (null != globalApiKeyId && null == selectedApiKey) {
            loadData();
            fetchOHLC();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalApiKeyId]);

    const handleAlgorithmDetect = useCallback(async () => {
        try {
            const results = await detectPatterns(symbol, interval, 'OKX', 50, 'ALGORITHM');
            setAlgorithmPatterns(results);
            setSelectedAlgorithmPatternId(prev => results.some(item => item.id === prev) ? prev : null);
            if (0 === results.length) message.info('当前周期暂无已确认的斐波那契回撤');
        } catch (error) {
            console.error('检测算法形态失败', error);
            message.error('算法形态检测失败，请稍后重试');
        }
    }, [symbol, interval]);

    // 轮询只在活动信号确认已离开当前结果集时收起报告；同 ID 刷新保持选择、报告与滚动状态。
    useEffect(() => {
        const key = aiState.expandedAnalysisKey;
        if (null == key) return;
        const id = Number(key.split(':')[1]);
        const stillPresent = key.startsWith('PATTERN:')
            ? patterns.some(pattern => pattern.id === id)
            : key.startsWith('TREND:')
                ? trendPatterns.some(tp => tp.id === id)
                : key.startsWith('ALGORITHM:')
                    ? algorithmPatterns.some(ap => ap.id === id)
                    : zoneResults.some(zone => zone.id === id);
        if (!stillPresent) dispatch({type: 'COLLAPSE_ANALYSIS'});
    }, [patterns, zoneResults, trendPatterns, algorithmPatterns, aiState.expandedAnalysisKey]);

    // 历史回测数据（切换到回测Tab时加载）
    const selectedPatternCode = selected?.patternCode ?? null;
    useEffect(() => {
        if ('history' !== activeTab || null == selectedPatternCode) return;
        getBacktestStats(selectedPatternCode, symbol, interval).then(stats => {
            if (stats) setBacktest(stats);
        }).catch(() => {
        });
    }, [activeTab, selectedPatternCode, symbol, interval]);

    // 实时价格 — 交易所Ticker每10s静默刷新
    useEffect(() => {
        // 品种/API Key 变化后，旧报价立即失效；请求序号用于丢弃切换前尚未返回的响应。
        setRealtimeQuote(null);
        const effectRequestSeq = ++realtimeRequestSeqRef.current;
        let cancelled = false;
        const fetchPrice = () => {
            const requestSeq = ++realtimeRequestSeqRef.current;
            // POC 页面未登录：使用无需 apiKeyId 的全局展示专用端点，避免空 apiKeyId 触发 500
            // 登录用户：使用带 apiKeyId 的通用端点
            const priceReq = isPocPage
                ? tradingService.getGlobalDisplayRealtimePrice(symbol)
                : getRealtimePrice(symbol, apiKeyIdRef.current ?? undefined);
            priceReq.then(res => {
                if (cancelled || requestSeq !== realtimeRequestSeqRef.current) return;
                const data = (res as any)?.data ?? res;
                if (data?.lastPrice) {
                    setRealtimeQuote({
                        symbol,
                        price: Number(data.lastPrice),
                        change24h: null == data.changePercent24H ? null : Number(data.changePercent24H),
                    });
                }
            }).catch(() => {
            });
        };
        fetchPrice();
        const timer = setInterval(fetchPrice, 10000);
        return () => {
            cancelled = true;
            clearInterval(timer);
            // 使当前品种所有未完成请求立即过期。
            if (realtimeRequestSeqRef.current >= effectRequestSeq) {
                realtimeRequestSeqRef.current++;
            }
        };
    }, [symbol, selectedApiKey]);

    // 智能 K线刷新 — 5m每20秒轮询，长周期按时间帧间隔 + 随机延迟
    useEffect(() => {
        if (klineTimerRef.current) clearTimeout(klineTimerRef.current);
        if ('5m' === interval) {
            const poll = () => {
                fetchOHLC();
                lastKlineRefreshRef.current = Date.now();
                klineTimerRef.current = setTimeout(poll, 20000);
            };
            klineTimerRef.current = setTimeout(poll, 20000);
        } else {
            const intervals: Record<string, number> = {'1H': 3600, '4H': 14400, '1D': 86400};
            const baseSec = intervals[interval] ?? 3600;
            const delaySec = baseSec + 10 + Math.floor(Math.random() * 10);
            klineTimerRef.current = setTimeout(() => {
                fetchOHLC();
                lastKlineRefreshRef.current = Date.now();
            }, delaySec * 1000);
        }
        return () => {
            if (klineTimerRef.current) clearTimeout(klineTimerRef.current);
        };
    }, [interval, fetchOHLC]);

    // 实时价格越界检测 — 超最新K线范围时，若距下次刷新 >2min 则立即刷新
    useEffect(() => {
        if (null == rtPrice || null == latestCandle) return;
        const exceeded = rtPrice > latestCandle.h || rtPrice < latestCandle.l;
        if (!exceeded) return;
        const elapsed = Date.now() - lastKlineRefreshRef.current;
        const intervals: Record<string, number> = {'1m': 60, '5m': 300, '1H': 3600, '4H': 14400, '1D': 86400};
        const baseSec = intervals[interval] ?? 3600;
        const periodMs = (baseSec + 10 + 5) * 1000; // 预估下次刷新时间
        const remaining = periodMs - elapsed;
        if (remaining > 120000) { // 距下次刷新 >2min
            fetchOHLC();
            lastKlineRefreshRef.current = Date.now();
        }
    }, [rtPrice]);

    // 数据获取 — 用户操作/首次加载显示loading，周期性刷新（aiActive控制轮询）
    useEffect(() => {
        const fetchWithLoading = async () => {
            setLoading(true);
            setChartDataIdentity(null);
            setRawCandles([]);
            setLatestCandle(null);
            setIndicators(undefined);
            setChartError(null);
            setPatterns([]);
            setTrendPatterns([]);
            setAlgorithmPatterns([]);
            setZoneResults([]);
            await fetchOHLC();
            await loadData();
            setLoading(false);
        };
        fetchWithLoading();
        if (!aiActive) return;
        // K线刷新周期：1m每10秒、5m每20秒刷新，长周期按基础间隔60s
        const refreshInterval = '1m' === interval ? 10000 : ('5m' === interval ? 20000 : 60000);
        const timer = setInterval(() => {
            loadData();
            fetchOHLC();
        }, refreshInterval);
        return () => clearInterval(timer);
    }, [loadData, fetchOHLC, aiActive, interval]);

    // 切换 Tab 时立即拉取数据
    useEffect(() => {
        if ('patterns' === activeTab || 'zone' === activeTab || 'trend' === activeTab || 'algorithm' === activeTab) {
            loadData();
        }
    }, [activeTab]);

    // 指标配置变化时先同步更新 ref，再拉取数据
    const indicatorConfigFingerprint = JSON.stringify(indicatorConfigs.map(c => ({
        k: c.key,
        p: c.periods.map(p => p.value + ':' + p.checked)
    })));
    useEffect(() => {
        // 同步更新 indicatorParamsRef（先于 fetchOHLC 确保最新参数）
        const params = indicatorConfigs.reduce((acc, ic) => {
            const checked = ic.periods.filter(p => p.checked);
            if (checked.length === 0) return acc;
            acc.indicators.push(ic.key);
            if ('SMA' === ic.key) acc.smaPeriods.push(...checked.map(p => Number(p.value)));
            if ('BOLL' === ic.key) acc.bollParams.push(...checked.map(p => p.value));
            if ('RSI' === ic.key) acc.rsiPeriods.push(...checked.map(p => Number(p.value)));
            if ('MACD' === ic.key) acc.macdPeriods.push(...checked.flatMap(p => p.value.split('_').map(Number)));
            return acc;
        }, {
            indicators: [] as string[],
            smaPeriods: [] as number[],
            bollParams: [] as string[],
            rsiPeriods: [] as number[],
            macdPeriods: [] as number[]
        });
        indicatorParamsRef.current = params.indicators.length > 0 ? params : {
            indicators: ['RSI', 'MACD', 'BOLL', 'SMA'],
            smaPeriods: [7, 25],
            bollParams: ['20_2.0'],
            rsiPeriods: [14],
            macdPeriods: [12, 26, 9]
        };
        fetchOHLC();
    }, [indicatorConfigFingerprint]);

    // rawCandles → CandlestickData 转换
    const chartDataIsCurrent = chartDataIdentity === `${symbol}|${interval}`;
    const chartIndicators = chartDataIsCurrent ? indicators : undefined;
    const chartCandles: CandlestickData[] = (chartDataIsCurrent ? rawCandles : []).map(c => ({
        time: toUtcSeconds(c.timestamp) as Time,
        open: Number(c.open),
        high: Number(c.high),
        low: Number(c.low),
        close: Number(c.close),
        volume: Number(c.volume ?? 0),
    } as CandlestickData & { volume: number }));
    // Lightweight Charts 的时间轴由 series 数据点建立。AI 计划超出已有 K 线时，
    // 在主蜡烛 series 追加只含 time 的空白槽，使未来区域不被最后一根真实 K 线裁剪。
    const aiPlanTimelineEnd = useMemo(() => {
        let maxEnd: number | null = null;
        for (const plan of rawPlans) {
            if (plan.holdingRangeAvailable) {
                const end = plan.endTime + intervalMs * 5;
                if (null == maxEnd || end > maxEnd) maxEnd = end;
            }
        }
        return maxEnd;
    }, [rawPlans, intervalMs]);
    const candleTimelineData = appendFutureCandleWhitespace(
        chartCandles,
        intervalMs,
        aiPlanTimelineEnd,
    );

    // Series refs — 动态 key: "SMA:7", "BOLL:upper:20_2.0", "RSI:14", "MACD:dif", "MACD:dea", "MACD:hist"
    const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
    const indSeriesMapRef = useRef<Record<string, ISeriesApi<'Line' | 'Histogram'>>>({});
    const volSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
    const markersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
    const priceLineRef = useRef<any>(null);
    const zoneLineRefsRef = useRef<ISeriesApi<'Line' | 'Area' | 'Baseline'>[]>([]);
    // 仅在当前图表首次拿到有效 K 线时自动缩放。轮询更新不得覆盖用户缩放/滚动位置。
    const hasFittedChartDataRef = useRef(false);
    const indicatorsRef = useRef<CompleteChartData['indicators']>(undefined);
    const indicatorConfigsRef = useRef(indicatorConfigs);
    // 保持 ref 与 state 同步，供 crosshair 回调读取最新值
    useEffect(() => {
        indicatorsRef.current = chartIndicators;
    }, [chartIndicators]);
    useEffect(() => {
        indicatorConfigsRef.current = indicatorConfigs;
    }, [indicatorConfigs]);

    // ═══════════════ K线图初始化（品种/周期变化时重建） ═══════════════
    useEffect(() => {
        const chartEl = chartContainerRef.current;
        if (!chartEl) return;

        // 销毁旧图表
        if (chartRef.current) {
            chartRef.current.remove();
            chartRef.current = null;
            priceLineRef.current = null;
        }

        const w = chartEl.clientWidth;
        const h = chartEl.clientHeight;
        if (0 === w || 0 === h) return;

        // ── 单实例图表（K线 + Volume 融合） ──
        const chart = createChart(chartEl, {
            layout: {
                attributionLogo: false,
                background: {type: ColorType.Solid, color: CHART_COLORS.bg},
                textColor: CHART_COLORS.text,
            },
            grid: {
                vertLines: {color: CHART_COLORS.grid, style: 3},
                horzLines: {color: CHART_COLORS.grid, style: 3},
            },
            crosshair: {mode: 0},
            rightPriceScale: {borderColor: CHART_COLORS.grid},
            timeScale: {
                borderColor: CHART_COLORS.grid,
                timeVisible: true,
                secondsVisible: false,
                fixLeftEdge: true,
                tickMarkFormatter: (t: unknown) => formatTick(t as number, interval),
            },
            handleScroll: {vertTouchDrag: true, horzTouchDrag: false},
            handleScale: {axisDoubleClickReset: false},
            localization: {
                timeFormatter: (t: number) => formatTick(t, interval),
            },
            width: w,
            height: h,
        });
        chartRef.current = chart;

        const candleSeries = chart.addSeries(CandlestickSeries, {
            upColor: CHART_COLORS.bull,
            downColor: CHART_COLORS.bear,
            borderUpColor: CHART_COLORS.bull,
            borderDownColor: CHART_COLORS.bear,
            wickUpColor: CHART_COLORS.bull,
            wickDownColor: CHART_COLORS.bear,
        });
        candleSeries.setData(candleTimelineData);
        candleSeries.applyOptions({priceLineVisible: false, lastValueVisible: false});
        candleSeriesRef.current = candleSeries;

        // Markers 插件（形态标记）
        markersRef.current = createSeriesMarkers(candleSeries, []);

        // 动态创建指标 series（根据 indicatorConfigs）
        const newIndSeries: Record<string, ISeriesApi<'Line' | 'Histogram'>> = {};
        for (const ic of indicatorConfigs) {
            for (const ip of ic.periods) {
                if (!ip.checked) continue; // 未勾选的不创建 series，避免空 pane 残留
                const val = ip.value;
                const col = ip.color;
                if ('SMA' === ic.key) {
                    const s = chart.addSeries(LineSeries, {color: col, lineWidth: 1});
                    const smaData = indicatorsToSmaData(chartIndicators, Number(val));
                    if (smaData.length > 0) s.setData(smaData);
                    newIndSeries[`SMA:${val}`] = s;
                } else if ('BOLL' === ic.key) {
                    const parts = val.split('_');
                    const bollKey = `BOLL:${val}`;
                    const bollData = indicatorsToBollData(chartIndicators, val);
                    const up = chart.addSeries(LineSeries, {color: col, lineWidth: 1, lineStyle: 2});
                    const mid = chart.addSeries(LineSeries, {color: col, lineWidth: 1});
                    const lo = chart.addSeries(LineSeries, {color: col, lineWidth: 1, lineStyle: 2});
                    if (bollData) {
                        up.setData(bollData.upper);
                        mid.setData(bollData.middle);
                        lo.setData(bollData.lower);
                    }
                    newIndSeries[`${bollKey}:upper`] = up;
                    newIndSeries[`${bollKey}:middle`] = mid;
                    newIndSeries[`${bollKey}:lower`] = lo;
                } else if ('RSI' === ic.key) {
                    if (null == newIndSeries[`RSI:${val}`]) {
                        // 提前分配独立 pane（pane 2），避免与 MACD 共用导致空间被压扁；
                        // 参考 LightweightCandlestickChart 的 paneIndexMapRef 模式。
                        if (chart.panes().length < 3) {
                            chart.addPane();
                        }
                        const rsiPaneIndex = chart.panes().length - 1;
                        const s = chart.addSeries(LineSeries, {
                            color: col,
                            lineWidth: 1,
                            priceScaleId: 'rsi'
                        }, rsiPaneIndex);
                        const rsiData = indicatorsToRsiData(chartIndicators, Number(val));
                        if (rsiData.length > 0) s.setData(rsiData);
                        newIndSeries[`RSI:${val}`] = s;
                        // 第二个参数传入 paneIndex，确保 lightweight-charts 在正确 pane 查找 'rsi' priceScale。
                        chart.priceScale('rsi', rsiPaneIndex).applyOptions({
                            scaleMargins: {top: 0.1, bottom: 0.1},
                            visible: true
                        });
                        // RSI 超买/超卖参考线
                        s.createPriceLine({
                            price: 70,
                            color: 'rgba(255,82,82,0.4)',
                            lineWidth: 1,
                            lineStyle: 2,
                            axisLabelVisible: false,
                            title: ''
                        });
                        s.createPriceLine({
                            price: 30,
                            color: 'rgba(0,217,126,0.4)',
                            lineWidth: 1,
                            lineStyle: 2,
                            axisLabelVisible: false,
                            title: ''
                        });
                    }
                } else if ('MACD' === ic.key) {
                    const macdData = indicatorsToMacdData(chartIndicators, `macd_${val}`);
                    const pk = `MACD:${val}`;
                    // 确保 MACD pane（pane 1）存在；主图已是 pane 0。
                    if (chart.panes().length < 2) {
                        chart.addPane();
                    }
                    const macdPaneIndex = 1;
                    const dif = chart.addSeries(LineSeries, {color: col, lineWidth: 1}, macdPaneIndex);
                    const dea = chart.addSeries(LineSeries, {color: '#f5a623', lineWidth: 1}, macdPaneIndex);
                    const hist = chart.addSeries(HistogramSeries, {priceFormat: {type: 'volume'}}, macdPaneIndex);
                    if (macdData) {
                        dif.setData(macdData.dif);
                        dea.setData(macdData.dea);
                        hist.setData(macdData.macd);
                    }
                    // MACD 与 RSI 共用 pane 1；MACD 占据下半部分（top 0.5 起），
                    // 避免与 RSI(0-100) 数值范围互相挤压。
                    chart.priceScale('right', macdPaneIndex).applyOptions({
                        scaleMargins: {top: 0.55, bottom: 0.05},
                        autoScale: true
                    });
                    newIndSeries[`${pk}:dif`] = dif;
                    newIndSeries[`${pk}:dea`] = dea;
                    newIndSeries[`${pk}:hist`] = hist;
                }
            }
        }
        indSeriesMapRef.current = newIndSeries;

        // 区域形态由下方独立 Effect 管理。图表重建后先清空旧代引用，待
        // chartGeneration 发布后再由唯一入口恢复，避免初始化/更新双重创建。
        zoneLineRefsRef.current = [];

        // Volume — 与K线同 pane，占底部 ~20%
        const volData: HistogramData[] = chartCandles.map(c => {
            const v = (c as any).volume ?? 0;
            const isUp = c.close >= c.open;
            const isDoji = Math.abs(c.close - c.open) / (c.high - c.low + 0.0001) < 0.1;
            let color = isUp ? CHART_COLORS.volUp : CHART_COLORS.volDown;
            if (isDoji) color = CHART_COLORS.volDoji;
            return {time: c.time, value: v, color};
        });
        const volSeries = chart.addSeries(HistogramSeries, {
            priceFormat: {type: 'volume'},
            priceScaleId: '',
        });
        volSeries.priceScale().applyOptions({
            scaleMargins: {top: 0.8, bottom: 0},
        });
        volSeries.setData(volData);
        volSeriesRef.current = volSeries;

        // 隐藏所有系列的默认末端价格线
        Object.values(newIndSeries).forEach(s => {
            s.applyOptions({priceLineVisible: false, lastValueVisible: false});
        });
        volSeries.applyOptions({priceLineVisible: false, lastValueVisible: false});
        chart.timeScale().fitContent();
        hasFittedChartDataRef.current = 0 < chartCandles.length;
        // 基础 Series、初始视区全部就绪后再发布 generation，避免 overlay 随后被 fitContent 覆盖。
        setChartGeneration(prev => prev + 1);

        // 鼠标悬浮数据浮窗
        chart.subscribeCrosshairMove((param) => {
            if (!param.time || !param.point) {
                setCrosshair(null);
                return;
            }
            const cd = param.seriesData.get(candleSeries) as {
                open: number; high: number; low: number; close: number
            } | undefined;
            const vd = param.seriesData.get(volSeries) as { value: number } | undefined;
            const t = (param.time as number) * 1000;
            // 有K线数据时收集指标值，无数据（空白K线区域）只展示时间
            const indVals: Record<string, number | null> = {};
            if (cd) {
                const ind = indicatorsRef.current;
                const currentConfigs = indicatorConfigsRef.current;
                for (const ic of currentConfigs) {
                    for (const ip of ic.periods) {
                        const key = `${ic.key}:${ip.value}`;
                        if ('MACD' === ic.key) {
                            indVals[`${key}:dif`] = getMacdValAt(ind, t, 'dif', ip.value);
                            indVals[`${key}:dea`] = getMacdValAt(ind, t, 'dea', ip.value);
                            indVals[`${key}:hist`] = getMacdValAt(ind, t, 'macd', ip.value);
                        } else if ('BOLL' === ic.key) {
                            indVals[`${key}:mid`] = getBollMidAt(ind, t);
                        } else if ('SMA' === ic.key) {
                            indVals[key] = getIndValAt(ind, 'SMA', `sma_${ip.value}`, t);
                        } else if ('RSI' === ic.key) {
                            indVals[key] = getIndValAt(ind, 'RSI', `rsi_${ip.value}`, t);
                        }
                    }
                }
            }
            setCrosshair({
                time: t,
                ...(cd ? {
                    open: cd.open, high: cd.high, low: cd.low, close: cd.close,
                    volume: vd?.value ?? 0,
                } : {}),
                indVals,
                x: param.point.x, y: Math.max(param.point.y - 120, 10),
                chartW: chartEl.clientWidth,
            });
        });

        // ── ResizeObserver ──
        const handleResize = () => {
            if (chartRef.current && chartContainerRef.current) {
                chartRef.current.applyOptions({
                    width: chartContainerRef.current.clientWidth,
                    height: chartContainerRef.current.clientHeight,
                });
            }
        };
        const ro = new ResizeObserver(handleResize);
        ro.observe(chartEl);

        return () => {
            ro.disconnect();
            if (chartRef.current) {
                chartRef.current.remove();
                chartRef.current = null;
            }
            candleSeriesRef.current = null;
            priceLineRef.current = null;
            hasFittedChartDataRef.current = false;
        };
    }, [interval, symbol, pageHeight, indicatorConfigs]);

    // ═══════════════ K线数据更新（不重建图表，仅更新系列数据） ═══════════════
    useEffect(() => {
        if (candleSeriesRef.current) candleSeriesRef.current.setData(candleTimelineData);
        if (volSeriesRef.current) {
            volSeriesRef.current.setData(chartCandles.map(c => {
                const v = (c as any).volume ?? 0;
                const isUp = c.close >= c.open;
                const isDoji = Math.abs(c.close - c.open) / (c.high - c.low + 0.0001) < 0.1;
                let color = isUp ? CHART_COLORS.volUp : CHART_COLORS.volDown;
                if (isDoji) color = CHART_COLORS.volDoji;
                return {time: c.time, value: v, color};
            }));
        }
        // 动态更新指标
        const map = indSeriesMapRef.current;
        // SMA
        const smaCfg = indicatorConfigs.find(c => 'SMA' === c.key);
        if (smaCfg) {
            for (const ip of smaCfg.periods) {
                const s = map[`SMA:${ip.value}`];
                if (s) (s as ISeriesApi<'Line'>).setData(indicatorsToSmaData(chartIndicators, Number(ip.value)));
            }
        }
        // BOLL
        const bollCfg = indicatorConfigs.find(c => 'BOLL' === c.key);
        if (bollCfg) {
            for (const ip of bollCfg.periods) {
                const bd = indicatorsToBollData(chartIndicators, ip.value);
                const pk = `BOLL:${ip.value}`;
                (map[`${pk}:upper`] as ISeriesApi<'Line'> | undefined)?.setData(bd?.upper ?? []);
                (map[`${pk}:middle`] as ISeriesApi<'Line'> | undefined)?.setData(bd?.middle ?? []);
                (map[`${pk}:lower`] as ISeriesApi<'Line'> | undefined)?.setData(bd?.lower ?? []);
            }
        }
        // RSI
        const rsiCfg = indicatorConfigs.find(c => 'RSI' === c.key);
        if (rsiCfg) {
            for (const ip of rsiCfg.periods) {
                const s = map[`RSI:${ip.value}`];
                if (s) (s as ISeriesApi<'Line'>).setData(indicatorsToRsiData(chartIndicators, Number(ip.value)));
            }
        }
        // MACD
        const macdCfg = indicatorConfigs.find(c => 'MACD' === c.key);
        if (macdCfg) {
            for (const ip of macdCfg.periods) {
                const md = indicatorsToMacdData(chartIndicators, `macd_${ip.value}`);
                const pk = `MACD:${ip.value}`;
                if (md) {
                    (map[`${pk}:dif`] as ISeriesApi<'Line'>)?.setData(md.dif);
                    (map[`${pk}:dea`] as ISeriesApi<'Line'>)?.setData(md.dea);
                    (map[`${pk}:hist`] as ISeriesApi<'Histogram'>)?.setData(md.macd);
                } else {
                    (map[`${pk}:dif`] as ISeriesApi<'Line'>)?.setData([]);
                    (map[`${pk}:dea`] as ISeriesApi<'Line'>)?.setData([]);
                    (map[`${pk}:hist`] as ISeriesApi<'Histogram'>)?.setData([]);
                }
            }
        }
        if (chartRef.current && 0 < chartCandles.length && !hasFittedChartDataRef.current) {
            chartRef.current.timeScale().fitContent();
            hasFittedChartDataRef.current = true;
        }
    }, [rawCandles, indicators, chartDataIdentity, symbol, interval,
        aiPlanTimelineEnd, intervalMs]);

    // ═══════════════ 多选形态的K线标记 ═══════════════
    useEffect(() => {
        const m = markersRef.current;
        if (!m) return;
        const markers = Array.from(selectedPatternIds)
            .map(id => patterns.find(p => p.id === id))
            .filter((p): p is PatternDetectResult => !!p)
            .map(pattern => {
                const dir = pattern.direction;
                return {
                    time: toUtcSeconds(pattern.klineEndTime) as Time,
                    position: (1 === dir ? 'belowBar' : 'aboveBar') as 'belowBar' | 'aboveBar',
                    color: 1 === dir ? CHART_COLORS.bull : -1 === dir ? CHART_COLORS.bear : CHART_COLORS.amber,
                    shape: (1 === dir ? 'arrowUp' : -1 === dir ? 'arrowDown' : 'circle') as 'arrowUp' | 'arrowDown' | 'circle',
                    text: pattern.patternName,
                };
            });
        m.setMarkers(markers);
    }, [selectedPatternIds, patterns, rawCandles, chartGeneration]);

    // 区域形态更新（半透明背景+虚线；盘整箱体绘制上下边界）
    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;
        zoneLineRefsRef.current.forEach(s => {
            try {
                chart.removeSeries(s);
            } catch (_) {
                /* 图表已重建时旧 series 可能已由 chart.remove() 清理。 */
            }
        });
        const newSeries: ISeriesApi<any>[] = [];
        // 渲染所有选中的区域形态
        const selectedZones = zoneResults.filter(z => selectedZoneIds.has(z.id));
        for (let zi = 0; zi < selectedZones.length; zi++) {
            const z = selectedZones[zi];
            const isSupport = 'LOW' === z.zoneType;
            const isResistance = 'HIGH' === z.zoneType;
            const isRange = 'RANGE' === z.zoneType;
            // 每个区域根据索引微调透明度，叠加时便于区分
            const alphaOffset = Math.min(0.15 * zi, 0.45);
            if (isRange) {
                const startT = toUtcSeconds(z.zoneStartTime) as Time;
                const endT = toUtcSeconds(getZoneEndTime(z)) as Time;
                const baseAlpha = Math.min(0.3 + alphaOffset, 0.6);
                const fillAlpha = Math.min(0.15 + alphaOffset, 0.4);
                const boxFill = chart.addSeries(BaselineSeries, {
                    baseValue: {type: 'price', price: Number(z.zoneLower)},
                    relativeGradient: false,
                    topFillColor1: `rgba(255, 140, 0, ${baseAlpha})`,
                    topFillColor2: `rgba(255, 140, 0, ${fillAlpha})`,
                    topLineColor: `rgba(255, 140, 0, ${baseAlpha})`,
                    bottomFillColor1: 'transparent',
                    bottomFillColor2: 'transparent',
                    bottomLineColor: 'transparent',
                    lineWidth: 1, lineStyle: 2,
                    priceLineVisible: false,
                    lastValueVisible: false,
                });
                boxFill.setData([
                    {time: startT, value: Number(z.zoneUpper)},
                    {time: endT, value: Number(z.zoneUpper)},
                ]);
                const upperLine = chart.addSeries(LineSeries, {
                    color: CHART_COLORS.bull, lineWidth: 1, lineStyle: 2,
                    priceLineVisible: false, lastValueVisible: false,
                });
                upperLine.setData([
                    {time: startT, value: Number(z.zoneUpper)},
                    {time: endT, value: Number(z.zoneUpper)},
                ]);
                const lowerLine = chart.addSeries(LineSeries, {
                    color: CHART_COLORS.bear, lineWidth: 1, lineStyle: 2,
                    priceLineVisible: false, lastValueVisible: false,
                });
                lowerLine.setData([
                    {time: startT, value: Number(z.zoneLower)},
                    {time: endT, value: Number(z.zoneLower)},
                ]);
                newSeries.push(boxFill, upperLine, lowerLine);
                continue;
            }
            if (!isSupport && !isResistance) continue;
            // canvas 绘制不解析 CSS 变量，图表颜色必须用具体色值
            const zoneColor = isSupport ? CHART_COLORS.bull : CHART_COLORS.amber;
            const alphaHex = Math.round(Math.min(0.3 + alphaOffset, 0.6) * 255).toString(16).padStart(2, '0');
            const alphaHex2 = Math.round(Math.min(0.1 + alphaOffset, 0.35) * 255).toString(16).padStart(2, '0');
            const fill = chart.addSeries(AreaSeries, {
                lineColor: zoneColor,
                topColor: zoneColor + alphaHex,
                bottomColor: zoneColor + alphaHex2,
                lineWidth: 1, lineStyle: 2,
                priceLineVisible: false, lastValueVisible: false,
            });
            const startT = toUtcSeconds(z.zoneStartTime) as Time;
            const zoneEnd = getZoneEndTime(z);
            fill.setData([
                {time: startT, value: Number(z.zonePrice)},
                {time: toUtcSeconds(zoneEnd) as Time, value: Number(z.zonePrice)},
            ]);
            newSeries.push(fill);
        }
        zoneLineRefsRef.current = newSeries;
    }, [zoneResults, selectedZoneIds, chartGeneration]);

    // 趋势/算法形态结构线 overlay；算法 Item 点击后按持久化 rawData 复原。
    const activeStructuredRawData = useMemo(() => {
        if (null === activeViewId) return null;
        if ('trend' === activeViewId.type) return trendPatterns.find(p => p.id === activeViewId.id)?.rawData ?? null;
        if ('algorithm' === activeViewId.type) return algorithmPatterns.find(p => p.id === activeViewId.id)?.rawData ?? null;
        return null;
    }, [activeViewId, trendPatterns, algorithmPatterns]);
    const latestChartCandleTime = chartDataIsCurrent && rawCandles.length > 0
        ? rawCandles[rawCandles.length - 1].timestamp : undefined;
    useTrendPatternOverlay(chartRef.current, candleSeriesRef.current, activeStructuredRawData,
        null !== activeViewId && ('trend' === activeViewId.type || 'algorithm' === activeViewId.type),
        latestChartCandleTime);

    // 实时价格标签（右侧 y 轴显示价格，不画水平虚线）
    useEffect(() => {
        const candleSeries = candleSeriesRef.current;
        if (!candleSeries) return;
        if (priceLineRef.current) {
            candleSeries.removePriceLine(priceLineRef.current);
            priceLineRef.current = null;
        }
        if (null == rtPrice) return;
        priceLineRef.current = candleSeries.createPriceLine({
            price: rtPrice,
            color: CHART_COLORS.amber,
            lineWidth: 1,
            lineVisible: false,
            lineStyle: 2,
            axisLabelVisible: true,
            title: '',
        });
    }, [rtPrice, symbol, chartGeneration]);

    // 指标显隐切换 + 按需创建新系列（不重建图表）
    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;
        const map = indSeriesMapRef.current;
        const ind = indicatorsRef.current;
        for (const ic of indicatorConfigs) {
            for (const ip of ic.periods) {
                if ('MACD' === ic.key) {
                    const visible = ip.checked;
                    const pk = `MACD:${ip.value}`;
                    if (visible && !map[`${pk}:dif`]) {
                        const macdData = indicatorsToMacdData(ind, `macd_${ip.value}`);
                        const dif = chart.addSeries(LineSeries, {color: ip.color, lineWidth: 1}, 1);
                        const dea = chart.addSeries(LineSeries, {color: '#f5a623', lineWidth: 1}, 1);
                        const hist = chart.addSeries(HistogramSeries, {priceFormat: {type: 'volume'}}, 1);
                        if (macdData) {
                            dif.setData(macdData.dif);
                            dea.setData(macdData.dea);
                            hist.setData(macdData.macd);
                        }
                        dif.applyOptions({priceLineVisible: false, lastValueVisible: false});
                        dea.applyOptions({priceLineVisible: false, lastValueVisible: false});
                        hist.applyOptions({priceLineVisible: false, lastValueVisible: false});
                        map[`${pk}:dif`] = dif;
                        map[`${pk}:dea`] = dea;
                        map[`${pk}:hist`] = hist;
                    }
                    map[`${pk}:dif`]?.applyOptions({visible});
                    map[`${pk}:dea`]?.applyOptions({visible});
                    map[`${pk}:hist`]?.applyOptions({visible});
                } else if ('BOLL' === ic.key) {
                    const visible = ip.checked;
                    const bk = `BOLL:${ip.value}`;
                    if (visible && !map[`${bk}:upper`]) {
                        const bollData = indicatorsToBollData(ind, ip.value);
                        const up = chart.addSeries(LineSeries, {color: ip.color, lineWidth: 1, lineStyle: 2});
                        const mid = chart.addSeries(LineSeries, {color: ip.color, lineWidth: 1});
                        const lo = chart.addSeries(LineSeries, {color: ip.color, lineWidth: 1, lineStyle: 2});
                        if (bollData) {
                            up.setData(bollData.upper);
                            mid.setData(bollData.middle);
                            lo.setData(bollData.lower);
                        }
                        up.applyOptions({priceLineVisible: false, lastValueVisible: false});
                        mid.applyOptions({priceLineVisible: false, lastValueVisible: false});
                        lo.applyOptions({priceLineVisible: false, lastValueVisible: false});
                        map[`${bk}:upper`] = up;
                        map[`${bk}:middle`] = mid;
                        map[`${bk}:lower`] = lo;
                    }
                    map[`${bk}:upper`]?.applyOptions({visible});
                    map[`${bk}:middle`]?.applyOptions({visible});
                    map[`${bk}:lower`]?.applyOptions({visible});
                } else if ('RSI' === ic.key) {
                    const key = `RSI:${ip.value}`;
                    if (ip.checked && !map[key]) {
                        // 与图表初始化逻辑保持一致：分配独立 pane 避免与 MACD 挤在同一副图。
                        if (chart.panes().length < 3) {
                            chart.addPane();
                        }
                        const rsiPaneIndex = chart.panes().length - 1;
                        const s = chart.addSeries(LineSeries, {
                            color: ip.color,
                            lineWidth: 1,
                            priceScaleId: 'rsi'
                        }, rsiPaneIndex);
                        const rsiData = indicatorsToRsiData(ind, Number(ip.value));
                        if (rsiData.length > 0) s.setData(rsiData);
                        s.applyOptions({priceLineVisible: false, lastValueVisible: false});
                        // 与图表初始化逻辑保持一致，指定对应 paneIndex。
                        chart.priceScale('rsi', rsiPaneIndex).applyOptions({
                            scaleMargins: {top: 0.1, bottom: 0.1},
                            visible: true
                        });
                        s.createPriceLine({
                            price: 70,
                            color: 'rgba(255,82,82,0.4)',
                            lineWidth: 1,
                            lineStyle: 2,
                            axisLabelVisible: false,
                            title: ''
                        });
                        s.createPriceLine({
                            price: 30,
                            color: 'rgba(0,217,126,0.4)',
                            lineWidth: 1,
                            lineStyle: 2,
                            axisLabelVisible: false,
                            title: ''
                        });
                        map[key] = s;
                    }
                    map[key]?.applyOptions({visible: ip.checked});
                } else {
                    const key = `${ic.key}:${ip.value}`;
                    if (ip.checked && !map[key]) {
                        const s = chart.addSeries(LineSeries, {color: ip.color, lineWidth: 1});
                        const smaData = indicatorsToSmaData(ind, Number(ip.value));
                        if (smaData.length > 0) s.setData(smaData);
                        s.applyOptions({priceLineVisible: false, lastValueVisible: false});
                        map[key] = s;
                    }
                    map[key]?.applyOptions({visible: ip.checked});
                }
            }
        }
    }, [indicatorConfigs]);


    // ═══════════════ 价格变化计算 ═══════════════
    let priceChange: { val: string; cls: 'bull' | 'bear' } = {val: '0.00%', cls: 'bull'};
    if (latestCandle && chartCandles.length >= 2) {
        const prevClose = chartCandles[chartCandles.length - 2].close;
        const chg = ((latestCandle.c - prevClose) / prevClose * 100);
        priceChange = {
            val: (chg >= 0 ? '+' : '') + chg.toFixed(2) + '%',
            cls: chg >= 0 ? 'bull' : 'bear',
        };
    }
    const latestPrice = latestCandle ? formatPrice(latestCandle.c) : '--';
    const isPriceUp = latestCandle && chartCandles.length >= 2
        ? latestCandle.c >= chartCandles[chartCandles.length - 2].close : true;

    // 从 reducer 统一 AI 状态中按前缀筛选 PATTERN/ZONE/TREND/ALGORITHM 分析结果
    const patternAiEntries: [number, import('./pattern/types').AIAnalysis][] = [];
    const zoneAiEntries: [number, import('./pattern/types').AIAnalysis][] = [];
    const trendAiEntries: [number, import('./pattern/types').AIAnalysis][] = [];
    const algorithmAiEntries: [number, import('./pattern/types').AIAnalysis][] = [];
    for (const [k, v] of Object.entries(aiState.analysisByKey)) {
        if (k.startsWith('PATTERN:')) {
            patternAiEntries.push([Number(k.slice('PATTERN:'.length)), v]);
        } else if (k.startsWith('ZONE:')) {
            zoneAiEntries.push([Number(k.slice('ZONE:'.length)), v]);
        } else if (k.startsWith('TREND:')) {
            trendAiEntries.push([Number(k.slice('TREND:'.length)), v]);
        } else if (k.startsWith('ALGORITHM:')) {
            algorithmAiEntries.push([Number(k.slice('ALGORITHM:'.length)), v]);
        }
    }
    const tradePanelKey = aiState.tradeSession?.analysisKey ?? null;
    const tradePanelAnalysis = tradePanelKey ? aiState.analysisByKey[tradePanelKey] ?? null : null;
    const tradePanelContext = tradePanelKey ? resolveSignalExpiryContext(tradePanelKey) : null;

    // ═══════════════ 趋势分类筛选 ═══════════════
    const filteredTrendPatterns = 'ALL' === trendCategoryFilter
        ? trendPatterns
        : trendPatterns.filter(p => p.trendCategory === trendCategoryFilter);

    // ═══════════════ 渲染 ═══════════════
    return (
        <>
            <div className="pattern-page" ref={pageRef}
                 data-capabilities-status={aiState.capabilitiesStatus}
                 data-quick-trade-enabled="true"
                 data-ai-overlay-active={0 < rawPlans.length ? 'true' : 'false'}
                 style={pageHeight ? {height: pageHeight} : undefined}>
                <div className="main">

                    {/* ═══ 中间图表区 ═══ */}
                    <div className="center">

                        {/* 工具栏 */}
                        <div className="chart-toolbar">
                            {/* CEX 选择器 */}
                            <select className="pattern-select">
                                <option value="OKX">OKX</option>
                                <option value="Bybit" disabled>Bybit</option>
                                <option value="Hyperliquid" disabled>Hyperliquid</option>
                            </select>
                            {/* 模式选择器 */}
                            <select
                                className="pattern-select"
                                value={mode}
                                onChange={e => {
                                    const newMode = e.target.value as 'SPOT' | 'SWAP';
                                    setMode(newMode);
                                    // 模式切换后品种从 topNSymbols 中自动适配（topN API 会根据模式重新拉取）
                                }}
                            >
                                <option value="SWAP">合约</option>
                                <option value="SPOT" disabled={isPocPage}>现货</option>
                            </select>
                            {/* 品种选择器 */}
                            <select
                                className="pattern-select symbol-select"
                                value={symbol}
                                onChange={e => setSymbol(e.target.value)}
                            >
                                {topNSymbols.map(s => {
                                    const coinPrefix = s.split('-')[0];
                                    const isDisabled = isPocPage && !POC_SELECTABLE_COINS.includes(coinPrefix);
                                    return (
                                        <option key={s} value={s} disabled={isDisabled}>
                                            {(SYMBOL_DISPLAY[s] ?? s.replace('-USDT-SWAP', '/USDT').replace('-USDT', '/USDT'))}{'SWAP' === mode ? ' 永续' : ''}
                                        </option>
                                    );
                                })}
                            </select>
                            <div className="filterbar">
                                {INTERVALS.filter(iv => !isPocPage || '1H' === iv || '4H' === iv).map(iv => (
                                    <button
                                        key={iv}
                                        className={`filterbar-btn${interval === iv ? ' active' : ''}`}
                                        onClick={() => setIntervalState(iv)}
                                    >
                                        {iv}
                                    </button>
                                ))}
                            </div>
                            {(isAuthenticated && !isPocPage && apiKeys.length > 0) && (
                                <select
                                    className="pattern-select"
                                    value={selectedApiKey ?? undefined}
                                    onChange={e => setSelectedApiKey(Number(e.target.value))}
                                    style={{minWidth: 80}}
                                >
                                    {apiKeys.map(key => (
                                        <option key={key.keyId} value={key.keyId}>
                                            {key.keyName}
                                        </option>
                                    ))}
                                </select>
                            )}
                            <div className="toolbar-sep"/>
                            {/* 技术指标标签 */}
                            {indicatorConfigs.map(ic => {
                                const checkedCount = ic.periods.filter(p => p.checked).length;
                                const isActive = checkedCount > 0;
                                const isExpanded = expandedIndKey === ic.key;
                                return (
                                    <div key={ic.key} className="indicator-wrap"
                                         onMouseLeave={() => setExpandedIndKey(null)}>
                                        <div
                                            className={`indicator-tag${isActive ? ' active' : ''}`}
                                            style={{opacity: isActive ? 1 : 0.4, marginLeft: 0}}
                                            onClick={() => {
                                                const allOff = ic.periods.every(p => !p.checked);
                                                setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                    ...c, periods: c.periods.map(p => ({...p, checked: allOff}))
                                                } : c));
                                            }}
                                        >
                                        <span className="dot"
                                              style={{background: isActive ? ic.periods.find(p => p.checked)?.color ?? '#3d5469' : '#3d5469'}}/>
                                            <span
                                                className="ind-label">{ic.label}{checkedCount > 0 ? ` +${checkedCount}` : ''}</span>
                                            <span
                                                className="ind-arrow"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedIndKey(isExpanded ? null : ic.key);
                                                }}
                                            >{isExpanded ? '▴' : '▾'}</span>
                                        </div>
                                        {isExpanded && (
                                            <div className="ind-panel">
                                                <div
                                                    className="ind-panel-hint">{'main' === ic.pane ? '主图指标' : '副图指标'}</div>
                                                {ic.periods.map((ip, idx) => (
                                                    <div key={idx} className="ind-period-row">
                                                        <input type="checkbox" checked={ip.checked} onChange={() => {
                                                            setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                                ...c,
                                                                periods: c.periods.map((p, i) => i === idx ? {
                                                                    ...p,
                                                                    checked: !p.checked
                                                                } : p)
                                                            } : c));
                                                        }}/>
                                                        <span className="dot" style={{
                                                            background: ip.checked ? ip.color : '#3d5469',
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            display: 'inline-block'
                                                        }}/>
                                                        {'BOLL' === ic.key ? (
                                                            <>
                                                                <span className="ind-period-label">P:</span>
                                                                <InputNumber
                                                                    size="small" min={2} max={200} step={1}
                                                                    disabled={readonlyIndicators}
                                                                    value={Number(ip.value.split('_')[0] ?? 20)}
                                                                    onChange={v => {
                                                                        if (null == v) return;
                                                                        const std = ip.value.split('_')[1] ?? '2.0';
                                                                        const newVal = `${v}_${std}`;
                                                                        setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                                            ...c,
                                                                            periods: c.periods.map((p, i) => i === idx ? {
                                                                                ...p,
                                                                                value: newVal,
                                                                                label: newVal
                                                                            } : p)
                                                                        } : c));
                                                                    }}
                                                                    style={{width: 55}}
                                                                />
                                                                <span className="ind-period-label">σ:</span>
                                                                <InputNumber
                                                                    size="small" min={0.5} max={5} step={0.5}
                                                                    disabled={readonlyIndicators}
                                                                    value={Number(ip.value.split('_')[1] ?? 2.0)}
                                                                    onChange={v => {
                                                                        if (null == v) return;
                                                                        const period = ip.value.split('_')[0] ?? '20';
                                                                        const newVal = `${period}_${v.toFixed(1)}`;
                                                                        setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                                            ...c,
                                                                            periods: c.periods.map((p, i) => i === idx ? {
                                                                                ...p,
                                                                                value: newVal,
                                                                                label: newVal
                                                                            } : p)
                                                                        } : c));
                                                                    }}
                                                                    style={{width: 50}}
                                                                />
                                                            </>
                                                        ) : 'MACD' === ic.key ? (
                                                            <>
                                                                <InputNumber
                                                                    size="small" min={2} max={100} step={1}
                                                                    disabled={readonlyIndicators}
                                                                    value={Number(ip.value.split('_')[0] ?? 12)}
                                                                    onChange={v => {
                                                                        if (null == v) return;
                                                                        const parts = ip.value.split('_');
                                                                        const newVal = `${v}_${parts[1] ?? 26}_${parts[2] ?? 9}`;
                                                                        setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                                            ...c,
                                                                            periods: c.periods.map((p, i) => i === idx ? {
                                                                                ...p,
                                                                                value: newVal,
                                                                                label: newVal
                                                                            } : p)
                                                                        } : c));
                                                                    }}
                                                                    style={{width: 50}}
                                                                />
                                                                <InputNumber
                                                                    size="small" min={2} max={200} step={1}
                                                                    disabled={readonlyIndicators}
                                                                    value={Number(ip.value.split('_')[1] ?? 26)}
                                                                    onChange={v => {
                                                                        if (null == v) return;
                                                                        const parts = ip.value.split('_');
                                                                        const newVal = `${parts[0] ?? 12}_${v}_${parts[2] ?? 9}`;
                                                                        setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                                            ...c,
                                                                            periods: c.periods.map((p, i) => i === idx ? {
                                                                                ...p,
                                                                                value: newVal,
                                                                                label: newVal
                                                                            } : p)
                                                                        } : c));
                                                                    }}
                                                                    style={{width: 50}}
                                                                />
                                                                <InputNumber
                                                                    size="small" min={2} max={100} step={1}
                                                                    disabled={readonlyIndicators}
                                                                    value={Number(ip.value.split('_')[2] ?? 9)}
                                                                    onChange={v => {
                                                                        if (null == v) return;
                                                                        const parts = ip.value.split('_');
                                                                        const newVal = `${parts[0] ?? 12}_${parts[1] ?? 26}_${v}`;
                                                                        setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                                            ...c,
                                                                            periods: c.periods.map((p, i) => i === idx ? {
                                                                                ...p,
                                                                                value: newVal,
                                                                                label: newVal
                                                                            } : p)
                                                                        } : c));
                                                                    }}
                                                                    style={{width: 50}}
                                                                />
                                                            </>
                                                        ) : (
                                                            <InputNumber
                                                                size="small"
                                                                min={2}
                                                                max={200}
                                                                disabled={readonlyIndicators}
                                                                value={Number(ip.value)}
                                                                onChange={v => {
                                                                    if (null == v) return;
                                                                    setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                                        ...c,
                                                                        periods: c.periods.map((p, i) => i === idx ? {
                                                                            ...p,
                                                                            value: String(v),
                                                                            label: String(v)
                                                                        } : p)
                                                                    } : c));
                                                                }}
                                                                style={{width: 60}}
                                                            />
                                                        )}
                                                        {!readonlyIndicators && (
                                                            <span className="ind-period-del" onClick={() => {
                                                                setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                                    ...c, periods: c.periods.filter((_, i) => i !== idx)
                                                                } : c));
                                                            }}>×</span>
                                                        )}
                                                    </div>
                                                ))}
                                                {!readonlyIndicators && (
                                                    <div className="ind-period-add" onClick={() => {
                                                        const colors = ['#4d9fff', '#a78bfa', '#f5a623', '#00d97e', '#ff5252', '#f5a623', '#2f54eb', '#13c2c2'];
                                                        const usedColors = ic.periods.map(p => p.color);
                                                        const nextColor = colors.find(c => !usedColors.includes(c)) ?? colors[ic.periods.length % colors.length];
                                                        const newVal = 'MACD' === ic.key ? '12_26_9' : 'BOLL' === ic.key ? '20_2.0' : '14';
                                                        setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                            ...c,
                                                            periods: [...c.periods, {
                                                                value: newVal,
                                                                label: newVal,
                                                                checked: true,
                                                                color: nextColor
                                                            }]
                                                        } : c));
                                                    }}>+ 新增周期
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            <div className="toolbar-sep"/>
                            <div
                                className={`ai-badge${aiActive ? '' : ' inactive'}`}
                                onClick={() => setAiActive(prev => !prev)}
                                title="AI 实时识别中"
                            >
                                <div className={`led${aiActive ? '' : ' off'}`}/>
                                <span className="ai-badge-text">AI 实时识别中</span>
                            </div>
                        </div>

                        {/* K线图 */}
                        <div className="chart-area" ref={chartContainerRef}>
                            {/* 加载遮罩 */}
                            {loading && (
                                <div style={{
                                    position: 'absolute', inset: 0, zIndex: 10,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: 'rgba(8, 11, 15, 0.7)', backdropFilter: 'blur(2px)',
                                }}>
                                    <Spin size="large"/>
                                </div>
                            )}
                            {/* 无K线 / 加载失败提示（避免图表静默空白） */}
                            {!loading && 0 === chartCandles.length && (
                                <div style={{
                                    position: 'absolute', inset: 0, zIndex: 9,
                                    display: 'flex', flexDirection: 'column', gap: 10,
                                    alignItems: 'center', justifyContent: 'center',
                                    background: 'rgba(8, 11, 15, 0.55)',
                                    color: 'var(--pt-text-dim)', fontSize: 13,
                                }}>
                                    <span>{chartError ?? '暂无K线数据'}</span>
                                    {null != chartError && (
                                        <Button size="small" onClick={() => void fetchOHLC()}>重试</Button>
                                    )}
                                </div>
                            )}
                            {/* 鼠标悬浮数据浮窗 */}
                            {crosshair && (
                                <div className="crosshair-tooltip" style={{
                                    left: crosshair.x + 15 > crosshair.chartW - 260 ? crosshair.x - 250 : crosshair.x + 12,
                                    top: crosshair.y
                                }}>
                                    <div className="ct-row"><span className="ct-label">时间</span><span
                                        className="ct-val">{formatSignalTime(crosshair.time)}</span></div>
                                    {null != crosshair.open && (
                                        <>
                                            <div className="ct-row"><span className="ct-label">开</span><span
                                                className="ct-val">{formatPrice(crosshair.open)}</span></div>
                                            <div className="ct-row"><span className="ct-label">高</span><span
                                                className="ct-val"
                                                style={{color: CHART_COLORS.bull}}>{formatPrice(crosshair.high)}</span>
                                            </div>
                                            <div className="ct-row"><span className="ct-label">低</span><span
                                                className="ct-val"
                                                style={{color: CHART_COLORS.bear}}>{formatPrice(crosshair.low)}</span>
                                            </div>
                                            <div className="ct-row"><span className="ct-label">收</span><span
                                                className="ct-val"
                                                style={{color: crosshair.close! >= crosshair.open! ? CHART_COLORS.bull : CHART_COLORS.bear}}>{formatPrice(crosshair.close)}</span>
                                            </div>
                                            <div className="ct-row"><span className="ct-label">量</span><span
                                                className="ct-val">{crosshair.volume! > 1000 ? (crosshair.volume! / 1000).toFixed(1) + 'K' : crosshair.volume!.toFixed(0)}</span>
                                            </div>
                                            <div className="ct-sep"/>
                                        </>
                                    )}
                                    {/* 动态技术指标值 */}
                                    {null != crosshair.open && indicatorConfigs.flatMap(ic =>
                                        ic.periods.filter(p => p.checked).flatMap(ip => {
                                            const key = `${ic.key}:${ip.value}`;
                                            // 统一格式: xxx(yyy)
                                            const paramStr = ip.value.replace(/_/g, ',');
                                            const color = ip.color;
                                            const baseLabel = `${ic.key}(${paramStr})`;
                                            if ('MACD' === ic.key) {
                                                const difKey = `${key}:dif`;
                                                const deaKey = `${key}:dea`;
                                                const histKey = `${key}:hist`;
                                                const difVal = crosshair.indVals[difKey];
                                                const deaVal = crosshair.indVals[deaKey];
                                                const histVal = crosshair.indVals[histKey];
                                                if (null == difVal && null == deaVal && null == histVal) return [];
                                                const parts = [`D:${null != difVal ? difVal!.toFixed(2) : '-'}`];
                                                if (null != deaVal) parts.push(`S:${deaVal!.toFixed(2)}`);
                                                if (null != histVal) parts.push(`H:${histVal!.toFixed(2)}`);
                                                return [{label: baseLabel, color, val: parts.join(' ')}];
                                            }
                                            if ('BOLL' === ic.key) {
                                                const bKey = `${key}:mid`;
                                                return null != crosshair.indVals[bKey] ? [{
                                                    label: baseLabel,
                                                    color,
                                                    val: formatPrice(Number(crosshair.indVals[bKey]!))
                                                }] : [];
                                            }
                                            if ('RSI' === ic.key) {
                                                return null != crosshair.indVals[key] ? [{
                                                    label: baseLabel,
                                                    color,
                                                    val: Number(crosshair.indVals[key]!).toFixed(2)
                                                }] : [];
                                            }
                                            return null != crosshair.indVals[key] ? [{
                                                label: baseLabel,
                                                color,
                                                val: formatPrice(Number(crosshair.indVals[key]!))
                                            }] : [];
                                        })
                                    ).map((item, i) => (
                                        <div key={i} className="ct-row">
                                            <span className="ct-label" style={{color: item.color}}>{item.label}</span>
                                            <span className="ct-val" style={{color: item.color}}>{item.val}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* OHLCV + 实时价格浮动数据 */}
                            {latestCandle && (
                                <div className="ohlcv-row">
                <span className={`ohlcv-price${null != rtPrice ? ' breathing' : ''}`}
                      style={rtPrice && (rtPrice > latestCandle.h || rtPrice < latestCandle.l) ? {borderBottom: '1px dashed #f5a623'} : undefined}>
                  {null != rtPrice ? formatPrice(rtPrice) : latestPrice}
                </span>
                                    <span
                                        className={`ohlcv-chg${null != rtChange24h && rtChange24h < 0 ? ' down' : ''}`}>
                  {null != rtChange24h ? (rtChange24h >= 0 ? '▲' : '▼') : isPriceUp ? '▲' : '▼'}{' '}
                                        {null != rtChange24h ? (rtChange24h >= 0 ? '+' : '') + rtChange24h.toFixed(2) + '%' : priceChange.val}
                </span>
                                    <span className="ohlcv-item">
                  <span className="ohlcv-label">开</span>
                  <span className="ohlcv-val">{formatPrice(latestCandle.o)}</span>
                </span>
                                    <span className="ohlcv-item">
                  <span className="ohlcv-label">高</span>
                  <span className="ohlcv-val">{formatPrice(latestCandle.h)}</span>
                </span>
                                    <span className="ohlcv-item">
                  <span className="ohlcv-label">低</span>
                  <span className="ohlcv-val">{formatPrice(latestCandle.l)}</span>
                </span>
                                    <span className="ohlcv-item">
                  <span className="ohlcv-label">量</span>
                  <span className="ohlcv-val" style={{color: '#f5a623'}}>
                    {latestCandle.v > 1000
                        ? (latestCandle.v / 1000).toFixed(1) + 'K'
                        : latestCandle.v.toFixed(0)}
                  </span>
                </span>
                                </div>
                            )}

                        </div>

                    </div>

                    {/* ═══ 右侧面板 ═══ */}
                    <div className="sidebar-right">

                        {/* Tab 头 */}
                        <div className="panel-header">
                            <button
                                className={`panel-tab${'patterns' === activeTab ? ' active' : ''}`}
                                onClick={() => setActiveTab('patterns')}
                            >
                                形态信号
                            </button>
                            <button
                                className={`panel-tab${'zone' === activeTab ? ' active' : ''}`}
                                onClick={() => setActiveTab('zone')}
                            >
                                区域形态
                            </button>
                            <button
                                className={`panel-tab${'trend' === activeTab ? ' active' : ''}`}
                                onClick={() => setActiveTab('trend')}
                            >
                                趋势形态
                            </button>
                            <button
                                className={`panel-tab${'algorithm' === activeTab ? ' active' : ''}`}
                                onClick={() => setActiveTab('algorithm')}
                            >
                                算法形态
                            </button>
                            <button
                                className={`panel-tab${'ai' === activeTab ? ' active' : ''}`}
                                onClick={() => setActiveTab('ai')}
                            >
                                AI 解读
                            </button>
                            <button
                                className={`panel-tab${'history' === activeTab ? ' active' : ''}`}
                                onClick={() => setActiveTab('history')}
                            >
                                历史回测
                            </button>
                        </div>

                        {/* 选中数量提示 */}
                        {('patterns' === activeTab || 'zone' === activeTab || 'trend' === activeTab || 'algorithm' === activeTab) && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                height: 22,
                                padding: '0 12px',
                                borderBottom: '1px solid var(--pt-border)',
                                flexShrink: 0,
                            }}>
                                <span style={{
                                    fontSize: 10,
                                    fontFamily: 'var(--pt-font-mono)',
                                    color: 'var(--pt-bull)',
                                }}>
                                    {'patterns' === activeTab
                                        ? `已选 ${selectedPatternIds.size}/${MAX_SELECTION} 个形态`
                                        : 'trend' === activeTab
                                            ? `已选 ${selectedTrendPatternIds.size}/${MAX_SELECTION} 个趋势`
                                            : 'algorithm' === activeTab
                                                ? `已选 ${null == selectedAlgorithmPatternId ? 0 : 1} 个算法形态`
                                                : `已选 ${selectedZoneIds.size}/${MAX_SELECTION} 个区域`
                                    }
                                </span>
                            </div>
                        )}

                        {/* ── 形态信号 Tab ── */}
                        {('patterns' === activeTab) && (
                            <div className="panel-body">
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: 2
                                }}>
                <span style={{fontSize: 11, color: 'var(--pt-text-dim)', fontFamily: 'var(--pt-font-mono)'}}>
                  {KLINE_COUNT[interval] ?? '?'} 根K线 · 检测到 {patterns.length} 个形态
                </span>
                                    <span style={{
                                        fontSize: 10,
                                        color: 'var(--pt-bull)',
                                        fontFamily: 'var(--pt-font-mono)'
                                    }}>全部</span>
                                </div>

                                {patterns.map(p => {
                                    const patternKey = ('PATTERN:' + p.id) as unknown as `PATTERN:${number}`;
                                    const aiData = aiState.analysisByKey[patternKey];
                                    const isExpanded = aiState.expandedAnalysisKey === patternKey;
                                    const isLoading = aiState.analysisRequestByKey[patternKey]?.status === 'loading';
                                    const missingPrices = !!aiData && hasMissingAiPrices(aiData);
                                    const validPlan = !aiData || isValidAiPriceRelation(aiData, p.direction as 1 | -1 | 0);
                                    const canOpenTrade = !aiData || missingPrices || validPlan;
                                    const tradeExpiry = aiData ? evaluateAnalysisExpiry(patternKey, aiData) : {
                                        expired: false,
                                        reason: null
                                    };
                                    return <PatternSignalCard
                                        key={p.id}
                                        pattern={p}
                                        selected={selectedPatternIds.has(p.id)}
                                        onSelect={() => {
                                            setSelectedPatternIds(prev => {
                                                if (prev.has(p.id)) {
                                                    const next = new Set(prev);
                                                    next.delete(p.id);
                                                    return next;
                                                }
                                                if (prev.size >= MAX_SELECTION) {
                                                    message.warning('最多同时选择' + MAX_SELECTION + '个形态');
                                                    return prev;
                                                }
                                                const next = new Set(prev);
                                                next.add(p.id);
                                                return next;
                                            });
                                        }}
                                        onViewChart={() => {
                                            handleViewChartToggle('pattern', p.id, p.klineStartTime ?? p.klineEndTime, p.klineEndTime);
                                        }}
                                        viewChartActive={activeViewId?.type === 'pattern' && activeViewId?.id === p.id}
                                        interval={interval}
                                        aiData={aiData}
                                        aiLoading={isLoading}
                                        aiError={aiState.analysisRequestByKey[patternKey]?.error}
                                        isExpanded={isExpanded}
                                        onAiClick={() => void handlePatternAiClick(p, patternKey)}
                                        canUseAiAnalysis={canTriggerAiAnalysis}
                                        isPocPage={isPocPage}
                                        quickTradeEnabled={'SWAP' === mode
                                            && canOpenTrade && !tradeExpiry.expired && !submissionUnresolved}
                                        validationError={missingPrices
                                            ? 'AI 未提供完整价格，图层暂不绘制且无法快捷下单'
                                            : validPlan ? null : 'AI 价格关系与信号方向不一致，已停止图表绘制和快捷下单'}
                                        expiryReason={tradeExpiry.expired ? tradeExpiry.reason : null}
                                        onGoTrade={aiData && canOpenTrade
                                        && !tradeExpiry.expired && !submissionUnresolved
                                            ? (_dir) => openAiTradePanel({
                                                key: patternKey, analysis: aiData,
                                                signalDirection: p.direction as 1 | -1 | 0,
                                                signalConfidence: p.confidence,
                                                tradeIntent: _dir,
                                            })
                                            : undefined}
                                    />;
                                })}

                                {patterns.length === 0 && (
                                    <div style={{textAlign: 'center', padding: 40, color: 'var(--pt-text-dim)'}}>
                                        暂无检测到的形态
                                    </div>
                                )}

                            </div>
                        )}

                        {/* ── 区域形态 Tab ── */}
                        {'zone' === activeTab && (
                            <div className="panel-body">
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: 2
                                }}>
                <span style={{fontSize: 11, color: 'var(--pt-text-dim)', fontFamily: 'var(--pt-font-mono)'}}>
                  检测到 {zoneResults.length} 个区域
                </span>
                                </div>

                                {zoneResults.map(z => {
                                    const zoneKey = ('ZONE:' + z.id) as unknown as `ZONE:${number}`;
                                    const zoneAiData = aiState.analysisByKey[zoneKey];
                                    const isZoneAiExpanded = aiState.expandedAnalysisKey === zoneKey;
                                    const isZoneAiLoading = aiState.analysisRequestByKey[zoneKey]?.status === 'loading';
                                    const missingPrices = !!zoneAiData && hasMissingAiPrices(zoneAiData);
                                    const validPlan = !zoneAiData || isValidAiPriceRelation(zoneAiData, z.direction as 1 | -1 | 0);
                                    const canOpenTrade = !zoneAiData || missingPrices || validPlan;
                                    const tradeExpiry = zoneAiData ? evaluateAnalysisExpiry(zoneKey, zoneAiData) : {
                                        expired: false,
                                        reason: null
                                    };
                                    return <ZoneSignalCard
                                        key={z.id}
                                        zone={z}
                                        selected={selectedZoneIds.has(z.id)}
                                        onSelect={() => {
                                            setSelectedZoneIds(prev => {
                                                if (prev.has(z.id)) {
                                                    const next = new Set(prev);
                                                    next.delete(z.id);
                                                    return next;
                                                }
                                                if (prev.size >= MAX_SELECTION) {
                                                    message.warning('最多同时选择' + MAX_SELECTION + '个区域');
                                                    return prev;
                                                }
                                                const next = new Set(prev);
                                                next.add(z.id);
                                                return next;
                                            });
                                        }}
                                        onViewChart={() => {
                                            handleViewChartToggle('zone', z.id, z.zoneStartTime, getZoneEndTime(z));
                                        }}
                                        viewChartActive={activeViewId?.type === 'zone' && activeViewId?.id === z.id}
                                        interval={interval}
                                        aiData={zoneAiData}
                                        aiLoading={isZoneAiLoading}
                                        aiError={aiState.analysisRequestByKey[zoneKey]?.error}
                                        isExpanded={isZoneAiExpanded}
                                        onAiClick={() => void handleZoneAiClick(z, zoneKey)}
                                        canUseAiAnalysis={canTriggerAiAnalysis}
                                        isPocPage={isPocPage}
                                        quickTradeEnabled={'SWAP' === mode
                                            && canOpenTrade && !tradeExpiry.expired && !submissionUnresolved}
                                        validationError={missingPrices
                                            ? 'AI 未提供完整价格，图层暂不绘制且无法快捷下单'
                                            : validPlan ? null : 'AI 价格关系与信号方向不一致，已停止图表绘制和快捷下单'}
                                        expiryReason={tradeExpiry.expired ? tradeExpiry.reason : null}
                                        onGoTrade={zoneAiData && canOpenTrade
                                        && !tradeExpiry.expired && !submissionUnresolved
                                            ? (_dir) => openAiTradePanel({
                                                key: zoneKey, analysis: zoneAiData,
                                                signalDirection: z.direction as 1 | -1 | 0,
                                                signalConfidence: z.confidence,
                                                tradeIntent: _dir,
                                            })
                                            : undefined}
                                    />;
                                })}

                                {0 === zoneResults.length && (
                                    <div style={{textAlign: 'center', padding: 40, color: 'var(--pt-text-dim)'}}>
                                        暂无检测到的区域形态
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── 趋势形态 Tab ── */}
                        {'trend' === activeTab && (
                            <div className="panel-body">
                                <div className="trend-filter-bar" style={{
                                    display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap'
                                }}>
                                    {([
                                        ['ALL', '全部'],
                                        ['REVERSAL', '反转形态'],
                                        ['CONTINUATION', '持续形态'],
                                        ['BILATERAL', '双边形态'],
                                    ] as const).map(([key, label]) => (
                                        <button key={key}
                                                className={`panel-tab trend-filter-tab${trendCategoryFilter === key ? ' active' : ''}`}
                                                style={{fontSize: 10, padding: '2px 8px'}}
                                                onClick={() => setTrendCategoryFilter(key)}
                                        >{label}</button>
                                    ))}
                                </div>
                                <div style={{
                                    fontSize: 11,
                                    color: 'var(--pt-text-dim)',
                                    fontFamily: 'var(--pt-font-mono)',
                                    marginBottom: 8
                                }}>
                                    检测到 {filteredTrendPatterns.length} 个趋势形态
                                </div>

                                {filteredTrendPatterns.map(p => {
                                    const trendKey = ('TREND:' + p.id) as unknown as `TREND:${number}`;
                                    const trendAiData = aiState.analysisByKey[trendKey];
                                    const isTrendAiExpanded = aiState.expandedAnalysisKey === trendKey;
                                    const isTrendAiLoading = aiState.analysisRequestByKey[trendKey]?.status === 'loading';
                                    const trendMissingPrices = !!trendAiData && hasMissingAiPrices(trendAiData);
                                    const trendValidPlan = !trendAiData || isValidAiPriceRelation(trendAiData, p.direction as 1 | -1 | 0);
                                    const trendCanOpenTrade = !trendAiData || trendMissingPrices || trendValidPlan;
                                    const trendTradeExpiry = trendAiData ? evaluateAnalysisExpiry(trendKey, trendAiData) : {
                                        expired: false,
                                        reason: null
                                    };
                                    return <TrendPatternCard
                                        key={p.id}
                                        pattern={p}
                                        selected={selectedTrendPatternIds.has(p.id)}
                                        viewChartActive={null !== activeViewId && 'trend' === activeViewId.type && activeViewId.id === p.id}
                                        onSelect={() => {
                                            setSelectedTrendPatternIds(prev => {
                                                if (prev.has(p.id)) {
                                                    const next = new Set(prev);
                                                    next.delete(p.id);
                                                    if (activeViewId?.type === 'trend' && activeViewId?.id === p.id) {
                                                        setActiveViewId(null);
                                                    }
                                                    return next;
                                                }
                                                if (prev.size >= MAX_SELECTION) {
                                                    message.warning('最多同时选择' + MAX_SELECTION + '个趋势');
                                                    return prev;
                                                }
                                                const next = new Set(prev);
                                                next.add(p.id);
                                                setActiveViewId({type: 'trend', id: p.id});
                                                return next;
                                            });
                                        }}
                                        onViewChart={() => {
                                            handleViewChartToggle('trend', p.id, p.klineStartTime ?? p.klineEndTime, p.klineEndTime);
                                        }}
                                        isAiAnalyzing={isTrendAiLoading}
                                        aiAnalysis={trendAiData}
                                        isAiExpanded={isTrendAiExpanded}
                                        onAiClick={() => void handleTrendAiClick(p, trendKey)}
                                        canTriggerAi={canTriggerAiAnalysis}
                                        isPocPage={isPocPage}
                                        interval={interval}
                                        requiredCandleCount={trendRequiredCandles[p.patternCode]}
                                        quickTradeEnabled={'SWAP' === mode
                                            && trendCanOpenTrade && !trendTradeExpiry.expired && !submissionUnresolved}
                                        validationError={trendMissingPrices
                                            ? 'AI 未提供完整价格，图层暂不绘制且无法快捷下单'
                                            : trendValidPlan ? null : 'AI 价格关系与信号方向不一致，已停止图表绘制和快捷下单'}
                                        expiryReason={trendTradeExpiry.expired ? trendTradeExpiry.reason : null}
                                        onGoTrade={trendAiData && trendCanOpenTrade
                                        && !trendTradeExpiry.expired && !submissionUnresolved
                                            ? (_dir) => openAiTradePanel({
                                                key: trendKey, analysis: trendAiData,
                                                signalDirection: p.direction as 1 | -1 | 0,
                                                signalConfidence: p.confidence,
                                                tradeIntent: _dir,
                                            })
                                            : undefined}
                                    />;
                                })}

                                {0 === filteredTrendPatterns.length && (
                                    <div style={{textAlign: 'center', padding: 40, color: 'var(--pt-text-dim)'}}>
                                        暂无检测到的趋势形态
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── 算法形态 Tab ── */}
                        {'algorithm' === activeTab && (
                            <div className="panel-body">
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: 8
                                }}>
                                    <span style={{
                                        fontSize: 11,
                                        color: 'var(--pt-text-dim)',
                                        fontFamily: 'var(--pt-font-mono)'
                                    }}>
                                        斐波那契回撤 · 检测到 {algorithmPatterns.length} 个独立区间
                                    </span>
                                    <button className="panel-tab" style={{fontSize: 10, padding: '2px 8px'}}
                                            onClick={() => void handleAlgorithmDetect()}>
                                        立即检测
                                    </button>
                                </div>
                                {algorithmPatterns.map(p => {
                                    const algorithmKey = ('ALGORITHM:' + p.id) as unknown as `ALGORITHM:${number}`;
                                    const algorithmAiData = aiState.analysisByKey[algorithmKey];
                                    const isAlgorithmAiExpanded = aiState.expandedAnalysisKey === algorithmKey;
                                    const isAlgorithmAiLoading = aiState.analysisRequestByKey[algorithmKey]?.status === 'loading';
                                    const algorithmMissingPrices = !!algorithmAiData && hasMissingAiPrices(algorithmAiData);
                                    const algorithmValidPlan = !algorithmAiData || isValidAiPriceRelation(algorithmAiData, p.direction as 1 | -1 | 0);
                                    const algorithmCanOpenTrade = !algorithmAiData || algorithmMissingPrices || algorithmValidPlan;
                                    const algorithmTradeExpiry = algorithmAiData ? evaluateAnalysisExpiry(algorithmKey, algorithmAiData) : {
                                        expired: false,
                                        reason: null
                                    };
                                    return <AlgorithmPatternCard
                                        key={p.id}
                                        pattern={p}
                                        selected={selectedAlgorithmPatternId === p.id}
                                        viewChartActive={activeViewId?.type === 'algorithm' && activeViewId.id === p.id}
                                        onSelect={() => {
                                            if (selectedAlgorithmPatternId === p.id) {
                                                setSelectedAlgorithmPatternId(null);
                                                setActiveViewId(null);
                                                return;
                                            }
                                            setSelectedAlgorithmPatternId(p.id);
                                            setActiveViewId({type: 'algorithm', id: p.id});
                                            centerOnSignal(p.klineStartTime ?? p.klineEndTime, p.klineEndTime);
                                        }}
                                        onViewChart={() => handleViewChartToggle('algorithm', p.id,
                                            p.klineStartTime ?? p.klineEndTime, p.klineEndTime)}
                                        isAiAnalyzing={isAlgorithmAiLoading}
                                        aiAnalysis={algorithmAiData}
                                        isAiExpanded={isAlgorithmAiExpanded}
                                        onAiClick={() => void handleAlgorithmAiClick(p, algorithmKey)}
                                        canTriggerAi={canTriggerAiAnalysis}
                                        isPocPage={isPocPage}
                                        interval={interval}
                                        quickTradeEnabled={'SWAP' === mode
                                            && algorithmCanOpenTrade && !algorithmTradeExpiry.expired && !submissionUnresolved}
                                        validationError={algorithmMissingPrices
                                            ? 'AI 未提供完整价格，图层暂不绘制且无法快捷下单'
                                            : algorithmValidPlan ? null : 'AI 价格关系与信号方向不一致，已停止图表绘制和快捷下单'}
                                        expiryReason={algorithmTradeExpiry.expired ? algorithmTradeExpiry.reason : null}
                                        onGoTrade={algorithmAiData && algorithmCanOpenTrade
                                        && !algorithmTradeExpiry.expired && !submissionUnresolved
                                            ? (_dir) => openAiTradePanel({
                                                key: algorithmKey, analysis: algorithmAiData,
                                                signalDirection: p.direction as 1 | -1 | 0,
                                                signalConfidence: p.confidence,
                                                tradeIntent: _dir,
                                            })
                                            : undefined}
                                    />;
                                })}
                                {0 === algorithmPatterns.length && (
                                    <div style={{textAlign: 'center', padding: 40, color: 'var(--pt-text-dim)'}}>
                                        暂无已确认的斐波那契回撤形态
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── AI 解读 Tab ── */}
                        {'ai' === activeTab && (
                            <div className="panel-body">
                                <div style={{
                                    fontSize: 11,
                                    color: 'var(--pt-text-dim)',
                                    fontFamily: 'var(--pt-font-mono)',
                                    marginBottom: 8
                                }}>
                                    AI 分析报告
                                    · {patternAiEntries.length + zoneAiEntries.length + trendAiEntries.length + algorithmAiEntries.length} 份
                                </div>
                                {(patternAiEntries.length === 0 && zoneAiEntries.length === 0 && trendAiEntries.length === 0 && algorithmAiEntries.length === 0) ? (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: 40,
                                        color: 'var(--pt-text-dim)',
                                        fontSize: 12
                                    }}>
                                        暂无 AI 分析报告，请在形态信号、区域形态、趋势形态或算法形态卡片中点击 🤖 触发分析
                                    </div>
                                ) : (
                                    <>
                                        {patternAiEntries.map(([pid, aiData]) => {
                                            const pat = patterns.find(p => p.id === pid);
                                            if (!pat) return null;
                                            const analysisKey = `PATTERN:${pid}` as const;
                                            const missingPrices = hasMissingAiPrices(aiData);
                                            const validPlan = isValidAiPriceRelation(aiData, pat.direction as 1 | -1 | 0);
                                            const tradeExpiry = evaluateAnalysisExpiry(analysisKey, aiData);
                                            const canTrade = 'SWAP' === mode && (missingPrices || validPlan)
                                                && !tradeExpiry.expired && !submissionUnresolved;
                                            return (
                                                <div key={pid} className="pattern-card">
                                                    <div className="pc-header">
                                                        <div className="pc-name">{pat.patternName}</div>
                                                        <div
                                                            className={`pc-dir ${directionClass(pat.direction)}`}>{directionLabel(pat.direction)}</div>
                                                    </div>
                                                    <div className="pc-meta">
                                                        <span>{interval} · {formatSignalTime(pat.klineEndTime)}</span>
                                                        <span
                                                            style={{color: 'var(--pt-text-dim)'}}>置信度 {pat.confidence}%</span>
                                                    </div>
                                                    <div className="metric-row" style={{marginTop: 8}}>
                                                        <div className="metric-box">
                                                            <div className="metric-label">入场</div>
                                                            <div
                                                                className="metric-val blue">${formatPrice(aiData.entryPrice)}</div>
                                                        </div>
                                                        <div className="metric-box">
                                                            <div className="metric-label">目标</div>
                                                            <div
                                                                className="metric-val bull">${formatPrice(aiData.targetPrice)}</div>
                                                        </div>
                                                        <div className="metric-box">
                                                            <div className="metric-label">止损</div>
                                                            <div
                                                                className="metric-val bear">${formatPrice(aiData.stopLossPrice)}</div>
                                                        </div>
                                                        <div className="metric-box">
                                                            <div className="metric-label">投入回报比</div>
                                                            <div
                                                                className="metric-val amber">1:{calculateProfitLossRatio(aiData.entryPrice ?? 0, aiData.targetPrice ?? 0, aiData.stopLossPrice ?? 0) || '--'}</div>
                                                        </div>
                                                    </div>
                                                    <div className="risk-bar-wrap" style={{marginTop: 6}}>
                                                        <div className="risk-label-row">
                                                        <span style={{
                                                            fontSize: 10,
                                                            fontFamily: 'var(--pt-font-mono)',
                                                            color: 'var(--pt-text-dim)'
                                                        }}>风险</span>
                                                            <span style={{
                                                                fontSize: 10,
                                                                fontFamily: 'var(--pt-font-mono)',
                                                                fontWeight: 600,
                                                                color: 'HIGH' === aiData.riskLevel ? 'var(--pt-bear)' : 'MEDIUM' === aiData.riskLevel ? 'var(--pt-amber)' : 'var(--pt-bull)'
                                                            }}>{'HIGH' === aiData.riskLevel ? '高' : 'MEDIUM' === aiData.riskLevel ? '中' : '低'}</span>
                                                        </div>
                                                        <div className="risk-track">
                                                            <div className="risk-fill"
                                                                 style={{width: 'HIGH' === aiData.riskLevel ? '75%' : 'MEDIUM' === aiData.riskLevel ? '48%' : '25%'}}/>
                                                        </div>
                                                    </div>
                                                    {(aiData.holdingPeriodText || aiData.expectedHoldingBars) && (
                                                        <div style={{
                                                            marginTop: 6,
                                                            fontSize: 10,
                                                            color: 'var(--pt-text-dim)'
                                                        }}>
                                                            预计持仓 {aiData.holdingPeriodText ?? `${aiData.expectedHoldingBars} 根K线`}
                                                        </div>
                                                    )}
                                                    <div className="ai-reasoning" style={{marginTop: 6}}>
                                                        <div className="ai-reasoning-title"><span
                                                            style={{color: 'var(--pt-amber)'}}>◈</span> AI 解读
                                                        </div>
                                                        <div className="ai-reasoning-body">{aiData.reasoning}</div>
                                                    </div>
                                                    {aiData.signals.length > 0 && (
                                                        <div className="signal-tag-row" style={{marginTop: 6}}>
                                                            {aiData.signals.map((s, i) => <span key={i}
                                                                                                className="signal-tag active-amber">{s}</span>)}
                                                        </div>
                                                    )}
                                                    {missingPrices
                                                        ? <div className="ai-qt-error">AI
                                                            未提供完整价格，图层暂不绘制且无法快捷下单</div>
                                                        : !validPlan && <div className="ai-qt-error">AI
                                                        价格关系与信号方向不一致，已停止图表绘制和快捷下单</div>}
                                                    {tradeExpiry.expired && <div
                                                        className="ai-qt-error">信号已过期：{tradeExpiry.reason}</div>}
                                                    <div style={{marginTop: 8, textAlign: 'right'}}>
                                                        <button className="pc-go-trade-btn"
                                                                disabled={!canTrade}
                                                                title={'SWAP' !== mode ? '快捷交易仅支持永续合约'
                                                                    : !missingPrices && !validPlan ? 'AI价格关系非法'
                                                                        : tradeExpiry.expired ? `信号已过期：${tradeExpiry.reason ?? ''}`
                                                                            : submissionUnresolved ? '当前订单结果尚未确认' : '去交易'}
                                                                onClick={() => openAiTradePanel({
                                                                    key: analysisKey,
                                                                    analysis: aiData,
                                                                    signalDirection: pat.direction as 1 | -1 | 0,
                                                                    signalConfidence: pat.confidence,
                                                                    tradeIntent: 'FORWARD',
                                                                })}>
                                                            {0 !== pat.direction
                                                                ? `${1 === pat.direction ? '开多' : '开空'} · AI正向`
                                                                : '去交易'}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {zoneAiEntries.length > 0 && (
                                            <div className="section-divider" style={{margin: '12px 0 8px'}}>
                                                <div className="section-title">区域形态 AI</div>
                                            </div>
                                        )}
                                        {zoneAiEntries.map(([zid, aiData]) => {
                                            const z = zoneResults.find(z => z.id === zid);
                                            if (!z) return null;
                                            const analysisKey = `ZONE:${zid}` as const;
                                            const missingPrices = hasMissingAiPrices(aiData);
                                            const validPlan = isValidAiPriceRelation(aiData, z.direction as 1 | -1 | 0);
                                            const tradeExpiry = evaluateAnalysisExpiry(analysisKey, aiData);
                                            const canTrade = 'SWAP' === mode && (missingPrices || validPlan)
                                                && !tradeExpiry.expired && !submissionUnresolved;
                                            const isSupport = 'LOW' === z.zoneType;
                                            const isResistance = 'HIGH' === z.zoneType;
                                            const isRange = 'RANGE' === z.zoneType;
                                            const zoneColor = isSupport ? 'var(--pt-bull)' : isResistance ? 'var(--pt-amber)' : 'var(--pt-blue)';
                                            const zoneLabel = isSupport ? '支撑区' : isResistance ? '阻力区' : '盘整箱体';
                                            return (
                                                <div key={zid} className="pattern-card">
                                                    <div className="pc-header">
                                                        <div className="pc-name">{z.patternName}</div>
                                                        <div style={{
                                                            fontSize: 10,
                                                            fontFamily: 'var(--pt-font-mono)',
                                                            fontWeight: 600,
                                                            padding: '2px 7px',
                                                            borderRadius: 2,
                                                            color: zoneColor,
                                                            background: isSupport ? 'var(--pt-bull-dim)' : isResistance ? 'var(--pt-amber-dim)' : 'var(--pt-blue-dim)',
                                                            border: `1px solid ${zoneColor}33`
                                                        }}>{zoneLabel}</div>
                                                    </div>
                                                    <div className="pc-meta">
                                                        <span>{interval} · {formatSignalTime(z.zoneStartTime)} → {formatSignalTime(z.zoneEndTime)}</span>
                                                        <span
                                                            style={{color: 'var(--pt-text-dim)'}}>强度 {z.confidence}%</span>
                                                    </div>
                                                    <div className="metric-row" style={{marginTop: 8}}>
                                                        <div className="metric-box">
                                                            <div className="metric-label">入场</div>
                                                            <div
                                                                className="metric-val blue">${formatPrice(aiData.entryPrice)}</div>
                                                        </div>
                                                        <div className="metric-box">
                                                            <div className="metric-label">目标</div>
                                                            <div
                                                                className="metric-val bull">${formatPrice(aiData.targetPrice)}</div>
                                                        </div>
                                                        <div className="metric-box">
                                                            <div className="metric-label">止损</div>
                                                            <div
                                                                className="metric-val bear">${formatPrice(aiData.stopLossPrice)}</div>
                                                        </div>
                                                        <div className="metric-box">
                                                            <div className="metric-label">投入回报比</div>
                                                            <div
                                                                className="metric-val amber">1:{calculateProfitLossRatio(aiData.entryPrice ?? 0, aiData.targetPrice ?? 0, aiData.stopLossPrice ?? 0) || '--'}</div>
                                                        </div>
                                                    </div>
                                                    <div className="risk-bar-wrap" style={{marginTop: 6}}>
                                                        <div className="risk-label-row">
                                                        <span style={{
                                                            fontSize: 10,
                                                            fontFamily: 'var(--pt-font-mono)',
                                                            color: 'var(--pt-text-dim)'
                                                        }}>风险</span>
                                                            <span style={{
                                                                fontSize: 10,
                                                                fontFamily: 'var(--pt-font-mono)',
                                                                fontWeight: 600,
                                                                color: 'HIGH' === aiData.riskLevel ? 'var(--pt-bear)' : 'MEDIUM' === aiData.riskLevel ? 'var(--pt-amber)' : 'var(--pt-bull)'
                                                            }}>{'HIGH' === aiData.riskLevel ? '高' : 'MEDIUM' === aiData.riskLevel ? '中' : '低'}</span>
                                                        </div>
                                                        <div className="risk-track">
                                                            <div className="risk-fill"
                                                                 style={{width: 'HIGH' === aiData.riskLevel ? '75%' : 'MEDIUM' === aiData.riskLevel ? '48%' : '25%'}}/>
                                                        </div>
                                                    </div>
                                                    {(aiData.holdingPeriodText || aiData.expectedHoldingBars) && (
                                                        <div style={{
                                                            marginTop: 6,
                                                            fontSize: 10,
                                                            color: 'var(--pt-text-dim)'
                                                        }}>
                                                            预计持仓 {aiData.holdingPeriodText ?? `${aiData.expectedHoldingBars} 根K线`}
                                                        </div>
                                                    )}
                                                    <div className="ai-reasoning" style={{marginTop: 6}}>
                                                        <div className="ai-reasoning-title"><span
                                                            style={{color: 'var(--pt-amber)'}}>◈</span> AI 解读
                                                        </div>
                                                        <div className="ai-reasoning-body">{aiData.reasoning}</div>
                                                    </div>
                                                    {aiData.signals.length > 0 && (
                                                        <div className="signal-tag-row" style={{marginTop: 6}}>
                                                            {aiData.signals.map((s, i) => <span key={i}
                                                                                                className="signal-tag active-amber">{s}</span>)}
                                                        </div>
                                                    )}
                                                    {missingPrices
                                                        ? <div className="ai-qt-error">AI
                                                            未提供完整价格，图层暂不绘制且无法快捷下单</div>
                                                        : !validPlan && <div className="ai-qt-error">AI
                                                        价格关系与信号方向不一致，已停止图表绘制和快捷下单</div>}
                                                    {tradeExpiry.expired && <div
                                                        className="ai-qt-error">信号已过期：{tradeExpiry.reason}</div>}
                                                    <div style={{marginTop: 8, textAlign: 'right'}}>
                                                        <button className="pc-go-trade-btn"
                                                                disabled={!canTrade}
                                                                title={'SWAP' !== mode ? '快捷交易仅支持永续合约'
                                                                    : !missingPrices && !validPlan ? 'AI价格关系非法'
                                                                        : tradeExpiry.expired ? `信号已过期：${tradeExpiry.reason ?? ''}`
                                                                            : submissionUnresolved ? '当前订单结果尚未确认' : '去交易'}
                                                                onClick={() => openAiTradePanel({
                                                                    key: analysisKey,
                                                                    analysis: aiData,
                                                                    signalDirection: z.direction as 1 | -1 | 0,
                                                                    signalConfidence: z.confidence,
                                                                    tradeIntent: 'FORWARD',
                                                                })}>
                                                            {0 !== z.direction
                                                                ? `${1 === z.direction ? '开多' : '开空'} · AI正向`
                                                                : '去交易'}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* 趋势形态 AI 报告 */}
                                        {trendAiEntries.map(([tid, aiData]) => {
                                            const trend = trendPatterns.find(t => t.id === tid);
                                            if (!trend) return null;
                                            const analysisKey = `TREND:${tid}` as const;
                                            const missingPrices = hasMissingAiPrices(aiData);
                                            const validPlan = isValidAiPriceRelation(aiData, trend.direction as 1 | -1 | 0);
                                            const tradeExpiry = evaluateAnalysisExpiry(analysisKey, aiData);
                                            const canTrade = 'SWAP' === mode && (missingPrices || validPlan)
                                                && !tradeExpiry.expired && !submissionUnresolved;
                                            const categoryLabel = trend.trendCategory === 'REVERSAL' ? '反转' :
                                                trend.trendCategory === 'CONTINUATION' ? '持续' : '双边';
                                            const trendColor = trend.direction === 1 ? 'var(--pt-bull)' :
                                                trend.direction === -1 ? 'var(--pt-bear)' : 'var(--pt-blue)';
                                            return (
                                                <div key={tid} className="pattern-card">
                                                    <div className="pc-header">
                                                        <div className="pc-name">{trend.patternName}</div>
                                                        <div style={{
                                                            fontSize: 10,
                                                            fontFamily: 'var(--pt-font-mono)',
                                                            fontWeight: 600,
                                                            padding: '2px 7px',
                                                            borderRadius: 2,
                                                            color: trendColor,
                                                            background: trend.direction === 1 ? 'var(--pt-bull-dim)' :
                                                                trend.direction === -1 ? 'var(--pt-bear-dim)' : 'var(--pt-blue-dim)',
                                                            border: `1px solid ${trendColor}33`
                                                        }}>{categoryLabel}</div>
                                                    </div>
                                                    <div className="pc-meta">
                                                        <span>{interval} · {formatSignalTime(trend.klineEndTime)}</span>
                                                        <span
                                                            style={{color: 'var(--pt-text-dim)'}}>置信度 {trend.confidence}%</span>
                                                    </div>

                                                    <div className="ai-metrics">
                                                        <div className="metric-box">
                                                            <div className="metric-label">入场</div>
                                                            <div
                                                                className="metric-val">${formatPrice(aiData.entryPrice)}</div>
                                                        </div>
                                                        <div className="metric-box">
                                                            <div className="metric-label">目标</div>
                                                            <div
                                                                className="metric-val bull">${formatPrice(aiData.targetPrice)}</div>
                                                        </div>
                                                        <div className="metric-box">
                                                            <div className="metric-label">止损</div>
                                                            <div
                                                                className="metric-val bear">${formatPrice(aiData.stopLossPrice)}</div>
                                                        </div>
                                                        <div className="metric-box">
                                                            <div className="metric-label">投入回报比</div>
                                                            <div
                                                                className="metric-val amber">1:{calculateProfitLossRatio(aiData.entryPrice ?? 0, aiData.targetPrice ?? 0, aiData.stopLossPrice ?? 0) || '--'}</div>
                                                        </div>
                                                    </div>
                                                    <div className="risk-bar-wrap" style={{marginTop: 6}}>
                                                        <div className="risk-label-row">
                                                        <span style={{
                                                            fontSize: 10,
                                                            fontFamily: 'var(--pt-font-mono)',
                                                            color: 'var(--pt-text-dim)'
                                                        }}>风险</span>
                                                            <span style={{
                                                                fontSize: 10,
                                                                fontFamily: 'var(--pt-font-mono)',
                                                                fontWeight: 600,
                                                                color: 'HIGH' === aiData.riskLevel ? 'var(--pt-bear)' : 'MEDIUM' === aiData.riskLevel ? 'var(--pt-amber)' : 'var(--pt-bull)'
                                                            }}>{'HIGH' === aiData.riskLevel ? '高' : 'MEDIUM' === aiData.riskLevel ? '中' : '低'}</span>
                                                        </div>
                                                        <div className="risk-track">
                                                            <div className="risk-fill"
                                                                 style={{width: 'HIGH' === aiData.riskLevel ? '75%' : 'MEDIUM' === aiData.riskLevel ? '48%' : '25%'}}/>
                                                        </div>
                                                    </div>
                                                    {(aiData.holdingPeriodText || aiData.expectedHoldingBars) && (
                                                        <div style={{
                                                            marginTop: 6,
                                                            fontSize: 10,
                                                            color: 'var(--pt-text-dim)'
                                                        }}>
                                                            预计持仓 {aiData.holdingPeriodText ?? `${aiData.expectedHoldingBars} 根K线`}
                                                        </div>
                                                    )}
                                                    <div className="ai-reasoning" style={{marginTop: 6}}>
                                                        <div className="ai-reasoning-title"><span
                                                            style={{color: 'var(--pt-amber)'}}>◈</span> AI 解读
                                                        </div>
                                                        <div className="ai-reasoning-body">{aiData.reasoning}</div>
                                                    </div>
                                                    {aiData.signals.length > 0 && (
                                                        <div className="signal-tag-row" style={{marginTop: 6}}>
                                                            {aiData.signals.map((s, i) => <span key={i}
                                                                                                className="signal-tag active-amber">{s}</span>)}
                                                        </div>
                                                    )}
                                                    {missingPrices
                                                        ? <div className="ai-qt-error">AI
                                                            未提供完整价格，图层暂不绘制且无法快捷下单</div>
                                                        : !validPlan && <div className="ai-qt-error">AI
                                                        价格关系与信号方向不一致，已停止图表绘制和快捷下单</div>}
                                                    {tradeExpiry.expired && <div
                                                        className="ai-qt-error">信号已过期：{tradeExpiry.reason}</div>}
                                                    <div style={{marginTop: 8, textAlign: 'right'}}>
                                                        <button className="pc-go-trade-btn"
                                                                disabled={!canTrade}
                                                                title={'SWAP' !== mode ? '快捷交易仅支持永续合约'
                                                                    : !missingPrices && !validPlan ? 'AI价格关系非法'
                                                                        : tradeExpiry.expired ? `信号已过期：${tradeExpiry.reason ?? ''}`
                                                                            : submissionUnresolved ? '当前订单结果尚未确认' : '去交易'}
                                                                onClick={() => openAiTradePanel({
                                                                    key: analysisKey,
                                                                    analysis: aiData,
                                                                    signalDirection: trend.direction as 1 | -1 | 0,
                                                                    signalConfidence: trend.confidence,
                                                                    tradeIntent: 'FORWARD',
                                                                })}>
                                                            {0 !== trend.direction
                                                                ? `${1 === trend.direction ? '开多' : '开空'} · AI正向`
                                                                : '去交易'}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* 算法形态 AI 报告 */}
                                        {algorithmAiEntries.length > 0 && (
                                            <div className="section-divider" style={{margin: '12px 0 8px'}}>
                                                <div className="section-title">算法形态 AI</div>
                                            </div>
                                        )}
                                        {algorithmAiEntries.map(([aid, aiData]) => {
                                            const algo = algorithmPatterns.find(a => a.id === aid);
                                            if (!algo) return null;
                                            const analysisKey = `ALGORITHM:${aid}` as const;
                                            const missingPrices = hasMissingAiPrices(aiData);
                                            const validPlan = isValidAiPriceRelation(aiData, algo.direction as 1 | -1 | 0);
                                            const tradeExpiry = evaluateAnalysisExpiry(analysisKey, aiData);
                                            const canTrade = 'SWAP' === mode && (missingPrices || validPlan)
                                                && !tradeExpiry.expired && !submissionUnresolved;
                                            const data = algo.rawData as AlgorithmPatternRawData | null;
                                            const ratio = resolveTouchedFibonacciRatio(data);
                                            return (
                                                <div key={aid} className="pattern-card">
                                                    <div className="pc-header">
                                                        <div className="pc-name">{algo.patternName}</div>
                                                        <div style={{
                                                            fontSize: 10,
                                                            fontFamily: 'var(--pt-font-mono)',
                                                            fontWeight: 600,
                                                            padding: '2px 7px',
                                                            borderRadius: 2,
                                                            color: algo.direction === 1 ? 'var(--pt-bull)' : 'var(--pt-bear)',
                                                            background: algo.direction === 1 ? 'var(--pt-bull-dim)' : 'var(--pt-bear-dim)',
                                                            border: `1px solid ${algo.direction === 1 ? 'var(--pt-bull)' : 'var(--pt-bear)'}33`
                                                        }}>斐波那契
                                                        </div>
                                                    </div>
                                                    <div className="pc-meta">
                                                        <span>{interval} · {formatSignalTime(algo.klineEndTime)}</span>
                                                        <span
                                                            style={{color: 'var(--pt-text-dim)'}}>置信度 {algo.confidence}%</span>
                                                    </div>
                                                    {null != ratio && (
                                                        <div style={{
                                                            fontSize: 10,
                                                            marginTop: 2,
                                                            color: 'var(--pt-text-dim)'
                                                        }}>
                                                            命中回撤 {(Number(ratio) * 100).toFixed(1)}%
                                                        </div>
                                                    )}
                                                    <div className="ai-metrics">
                                                        <div className="metric-box">
                                                            <div className="metric-label">入场</div>
                                                            <div
                                                                className="metric-val">${formatPrice(aiData.entryPrice)}</div>
                                                        </div>
                                                        <div className="metric-box">
                                                            <div className="metric-label">目标</div>
                                                            <div
                                                                className="metric-val bull">${formatPrice(aiData.targetPrice)}</div>
                                                        </div>
                                                        <div className="metric-box">
                                                            <div className="metric-label">止损</div>
                                                            <div
                                                                className="metric-val bear">${formatPrice(aiData.stopLossPrice)}</div>
                                                        </div>
                                                        <div className="metric-box">
                                                            <div className="metric-label">投入回报比</div>
                                                            <div
                                                                className="metric-val amber">1:{calculateProfitLossRatio(aiData.entryPrice ?? 0, aiData.targetPrice ?? 0, aiData.stopLossPrice ?? 0) || '--'}</div>
                                                        </div>
                                                    </div>
                                                    <div className="risk-bar-wrap" style={{marginTop: 6}}>
                                                        <div className="risk-label-row">
                                                            <span style={{
                                                                fontSize: 10,
                                                                fontFamily: 'var(--pt-font-mono)',
                                                                color: 'var(--pt-text-dim)'
                                                            }}>风险</span>
                                                            <span style={{
                                                                fontSize: 10,
                                                                fontFamily: 'var(--pt-font-mono)',
                                                                fontWeight: 600,
                                                                color: 'HIGH' === aiData.riskLevel ? 'var(--pt-bear)' : 'MEDIUM' === aiData.riskLevel ? 'var(--pt-amber)' : 'var(--pt-bull)'
                                                            }}>
                                                                {'HIGH' === aiData.riskLevel ? '高' : 'MEDIUM' === aiData.riskLevel ? '中' : '低'}
                                                            </span>
                                                        </div>
                                                        <div className="risk-track">
                                                            <div className="risk-fill"
                                                                 style={{width: 'HIGH' === aiData.riskLevel ? '75%' : 'MEDIUM' === aiData.riskLevel ? '48%' : '25%'}}/>
                                                        </div>
                                                    </div>
                                                    {(aiData.holdingPeriodText || aiData.expectedHoldingBars) && (
                                                        <div style={{
                                                            marginTop: 6,
                                                            fontSize: 10,
                                                            color: 'var(--pt-text-dim)'
                                                        }}>
                                                            预计持仓 {aiData.holdingPeriodText ?? `${aiData.expectedHoldingBars} 根K线`}
                                                        </div>
                                                    )}
                                                    <div className="ai-reasoning" style={{marginTop: 6}}>
                                                        <div className="ai-reasoning-title"><span
                                                            style={{color: 'var(--pt-amber)'}}>◈</span> AI 解读
                                                        </div>
                                                        <div className="ai-reasoning-body">{aiData.reasoning}</div>
                                                    </div>
                                                    {aiData.signals.length > 0 && (
                                                        <div className="signal-tag-row" style={{marginTop: 6}}>
                                                            {aiData.signals.map((s, i) => <span key={i}
                                                                                                className="signal-tag active-amber">{s}</span>)}
                                                        </div>
                                                    )}
                                                    {missingPrices
                                                        ? <div className="ai-qt-error">AI
                                                            未提供完整价格，图层暂不绘制且无法快捷下单</div>
                                                        : !validPlan && <div className="ai-qt-error">AI
                                                        价格关系与信号方向不一致，已停止图表绘制和快捷下单</div>}
                                                    {tradeExpiry.expired && <div
                                                        className="ai-qt-error">信号已过期：{tradeExpiry.reason}</div>}
                                                    <div style={{marginTop: 8, textAlign: 'right'}}>
                                                        <button className="pc-go-trade-btn"
                                                                disabled={!canTrade}
                                                                title={'SWAP' !== mode ? '快捷交易仅支持永续合约'
                                                                    : !missingPrices && !validPlan ? 'AI价格关系非法'
                                                                        : tradeExpiry.expired ? `信号已过期：${tradeExpiry.reason ?? ''}`
                                                                            : submissionUnresolved ? '当前订单结果尚未确认' : '去交易'}
                                                                onClick={() => openAiTradePanel({
                                                                    key: analysisKey,
                                                                    analysis: aiData,
                                                                    signalDirection: algo.direction as 1 | -1 | 0,
                                                                    signalConfidence: algo.confidence,
                                                                    tradeIntent: 'FORWARD',
                                                                })}>
                                                            {0 !== algo.direction
                                                                ? `${1 === algo.direction ? '开多' : '开空'} · AI正向`
                                                                : '去交易'}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                                <div className="risk-warning">
                                    <div className="risk-warning-title">⚠ 风险提示</div>
                                    <div
                                        className="risk-warning-body">本分析仅供参考，不构成投资建议。加密货币市场波动剧烈，请结合自身风险承受能力谨慎决策。
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── 历史回测 Tab ── */}
                        {'history' === activeTab && (
                            <div className="panel-body" style={{position: 'relative'}}>
                                <div className="backtest-mask">
                                    <span>即将开放</span>
                                </div>
                                {selected ? (
                                    <>
                                        <div className="bt-title">
                                            {selected.patternName} · {SYMBOL_DISPLAY[symbol] ?? symbol} {interval} · 近
                                            90
                                            天回测
                                        </div>

                                        {backtest ? (
                                            <>
                                                <div className="bt-stat-grid">
                                                    <div className="bt-stat-cell">
                                                        <div className="bt-stat-label">触发次数</div>
                                                        <div className="bt-stat-val">{backtest.totalTriggers}</div>
                                                    </div>
                                                    <div className="bt-stat-cell">
                                                        <div className="bt-stat-label">胜率</div>
                                                        <div className="bt-stat-val bull">{backtest.winRate}%</div>
                                                    </div>
                                                    <div className="bt-stat-cell">
                                                        <div className="bt-stat-label">平均盈亏比</div>
                                                        <div
                                                            className="bt-stat-val amber">{null != backtest.avgProfitRatio && 0 !== backtest.avgProfitRatio ? backtest.avgProfitRatio : '--'}</div>
                                                    </div>
                                                </div>

                                                {(backtest.totalTriggers > 0) && (
                                                    <div className="bt-winbar">
                                                        <div className="bt-winbar-wrap">
                                                            <div className="bt-winbar-labels">
                                                            <span
                                                                style={{color: 'var(--pt-bull)'}}>胜 {backtest.wins}</span>
                                                                <span
                                                                    style={{color: 'var(--pt-bear)'}}>负 {backtest.losses}</span>
                                                            </div>
                                                            <div className="bt-winbar-track">
                                                                <div className="bt-winbar-fill"
                                                                     style={{width: `${backtest.winRate}%`}}/>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {backtest.recentResults?.length > 0 && (
                                                    <>
                                                        <div className="bt-recent-title">最近触发记录</div>
                                                        <div className="bt-recent-list">
                                                            {backtest.recentResults.slice(0, 5).map((r, i) => (
                                                                <div key={i} className="bt-recent-row">
                                                                <span
                                                                    className="bt-recent-time">{formatSignalTime(r.time)}</span>
                                                                    <span
                                                                        className={`bt-recent-result ${'WIN' === r.result ? 'win' : 'loss'}`}>
                                  {r.pnl}
                                </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <div style={{
                                                textAlign: 'center',
                                                padding: 20,
                                                color: 'var(--pt-text-dim)',
                                                fontSize: 12
                                            }}>
                                                加载中...
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div style={{textAlign: 'center', padding: 60, color: 'var(--pt-text-dim)'}}>
                                        请先选择一个形态
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {aiState.submissionSnapshot && !aiState.tradePanelOpen && 'idle' !== aiState.submissionStatus && (
                <button
                    type="button"
                    className="ai-pending-order-entry"
                    disabled={pendingOrderQueryLoading}
                    onClick={() => dispatch({type: 'REOPEN_TRADE_SESSION'})}
                >
                    {pendingOrderQueryLoading ? '查询中…'
                        : 'succeeded' === aiState.submissionStatus ? '订单已成功'
                            : 'failed' === aiState.submissionStatus ? '订单处理失败'
                                : 'under_review' === aiState.submissionStatus ? '订单审核中'
                                    : '订单结果确认中'}
                </button>
            )}

            {aiState.submissionSnapshot && !aiState.tradeSession && (
                <PendingSignalOrderPanel
                    open={aiState.tradePanelOpen}
                    snapshot={aiState.submissionSnapshot}
                    status={aiState.submissionStatus}
                    message={aiState.submissionMessage}
                    querying={pendingOrderQueryLoading}
                    onQuery={queryPendingOrderStatus}
                    onClose={() => dispatch({type: 'CLOSE_TRADE_SESSION'})}
                />
            )}

            {tradePanelKey && tradePanelAnalysis && aiState.tradeSession && (
                <AiQuickTradingPanel
                    open={aiState.tradePanelOpen}
                    onClose={() => dispatch({type: 'CLOSE_TRADE_SESSION'})}
                    tradeSession={aiState.tradeSession}
                    submissionSnapshot={aiState.submissionSnapshot}
                    submissionStatus={aiState.submissionStatus}
                    submissionMessage={aiState.submissionMessage}
                    dispatch={dispatch}
                    apiKeyId={null == selectedApiKey ? null : Number(selectedApiKey)}
                    isLiveTrading={apiKeys.find(k => k.keyId === selectedApiKey)?.isLiveTrading ?? false}
                    instId={symbol}
                    interval={interval}
                    analysisKey={tradePanelKey}
                    aiAnalysisId={tradePanelAnalysis.id}
                    signalType={tradePanelKey.startsWith('ZONE:') ? 'ZONE' : tradePanelKey.startsWith('ALGORITHM:') ? 'ALGORITHM' : 'PATTERN'}
                    signalId={Number(tradePanelKey.split(':')[1])}
                    signalDirection={tradePanelAnalysis.signalDirection}
                    signalConfidence={tradePanelAnalysis.signalConfidence}
                    entryPrice={tradePanelAnalysis.entryPrice ?? 0}
                    targetPrice={tradePanelAnalysis.targetPrice ?? 0}
                    stopLossPrice={tradePanelAnalysis.stopLossPrice ?? 0}
                    expectedHoldingBars={tradePanelAnalysis.expectedHoldingBars}
                    expectedHoldingUntil={tradePanelAnalysis.expectedHoldingUntil}
                    holdingPeriodText={tradePanelAnalysis.holdingPeriodText}
                    analysisGeneratedAt={tradePanelAnalysis.analysisGeneratedAt}
                    signalFormedAt={tradePanelContext?.signalFormedAt ?? tradePanelAnalysis.analysisGeneratedAt}
                    currentPrice={rtPrice ?? latestCandle?.c ?? 0}
                    zoneType={tradePanelContext?.zoneType}
                    zoneUpper={tradePanelContext?.zoneUpper}
                    zoneLower={tradePanelContext?.zoneLower}
                    latestClose={latestCandle?.confirmedClose ?? undefined}
                    expiryPolicy={capabilities}
                />
            )}
        </>
    );
};
export default PatternRecognitionPage;
